// CGameScene — 牌局主畫面
// 對應 PRD §4.4 CGameScene、§5 一局狀態機、§9 版面規格
//
// === 使用圖片 ===
// 本 Scene 不再直接呼叫 add.image — 所有視覺由子元件處理（圖片清單請見對應檔頭）：
// - 桌面背景：createTableBackground（Graphics 程序化，無圖）
// - 左下剩牌計數：CWallCounter
// - 4 家手牌與副露：CHandView × 4 → 內部用 CTileSprite（進場直接完整顯示）
// - 中央捨牌池：CDiscardPool → 內部用 CTileSprite
// - 中央資訊面板（局數 / 底台）：CTableInfo
// - 三位 AI 牌友頭像、名字與對話泡泡：CSeatCharacters（assets/avatars/*.png）
// - 4 家風位徽章 + 莊家 icon + 自家 label：CSeatBadges
// - 動作按鈕條：CActionBar（含「聽」鈕；圖片清單見其檔頭）
// - 補花階段中央橫幅（花帶 + 補花徽章）：CFlowerBanner（圖片清單見其檔頭）
// - 聽牌瞇牌動畫（手掌 + 拇指掀牌）：CTenpaiPeek（圖片清單見其檔頭）
// - 宣告「聽」橫幅飛入動畫：CTenpaiBanner（圖片清單見其檔頭）
// - 自摸／胡／放槍儀式：CWinCeremony（圖片清單見其檔頭）
// - 流局儀式：CGameDrawCeremony（圖片清單見其檔頭）
//
// Stage 4+ 完成度：
// - IEventBus 串接 Core ↔ UI（CHandView / CDiscardPool / 動作按鈕 / 計時器）
// - 玩家單擊出牌：點手牌一次即出牌（原位放大牌升起 → 停留 → 淡出補位，無黃框、不需二次確認）
// - AI 思考延遲 500–1500ms
// - 動作按鈕（吃 / 碰 / 槓 / 胡 / 過 / 代打）
// - 聽牌提示 + 倒數計時 + 逾時 AI 代打
// - 視覺：牌底+圖案兩層合成、莊家標、剩牌計數、各家方位標

import Phaser from 'phaser';

import { createTableAIProfile } from '@core/table-ai';
import { CAudioManager } from '@audio/index';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '@config/game-config';
import type { TPlayerAction, TRoundEventMap } from '@core/events';
import { PlayerActionType } from '@core/events';
import { addToConcealed, type THand } from '@core/hand';
import {
  advanceGameSetAfterRound,
  canAllPlayersAfford,
  completeGameSetForInsufficientBalance,
  computeSettlement,
  GameSetCompletionReason,
  GameSetStatus,
  type IGameSetSession,
  type IRoundPlan,
} from '@core/index';
import { MeldType } from '@core/meld';
import { CRound, RoundPhase, type TRoundConfig } from '@core/round';
import type { IRoundDriver } from '@core/round-driver';
import { DiscardReactionType, findKongAddedOption, findKongConcealedOptions } from '@core/ruleset';
import { RoundSummaryKind, type TRoundSummary } from '@core/summary';
import { isFlower, type TTile } from '@core/tile';
import { Seat, Suit, Wind, type TSeat, type TWind } from '@core/types';
import { createSeedRng, liveWallRemaining } from '@core/wall';
import { traceGameSet } from '../debug/round-trace';
import type { IEventBus } from '@event-bus/event-bus';
import { createEventBus } from '@event-bus/event-bus';
import { CDiscardLandingGate } from '@input/discard-landing-gate';
import { CFlowerTurnGate } from '@input/flower-turn-gate';
import { CMeldTurnGate } from '@input/meld-turn-gate';
import {
  applySettlementTransaction,
  loadGameSave,
  loadSettings,
  updateSettings,
} from '@storage/index';
import {
  CNativeHandPointerFallback,
  shouldHandleHandTilePointerFallback,
} from '@input/hand-tile-hit-test';
import { shouldCollapsePlayerHandGap } from '@input/player-discard-motion';
import {
  ActionKey,
  CActionBar,
  CActionIcon,
  CDiscardAnnounce,
  CDiscardPool,
  CWallCounter,
  createTableBackground,
  CSeatBadges,
  CTableInfo,
  CTurnTimer,
  CPlayerStatus,
  CPauseDialog,
  CTurnIndicatorHand,
  CFlowerBanner,
  CGameDrawCeremony,
  CTenpaiPeek,
  CTenpaiBanner,
  CWinCeremony,
  SEAT_BADGE_SPOTS,
  buildWinCeremonyPlan,
  supportsHover,
  type CHandView,
  type ISelfDrawHandTuning,
  type TActionChoice,
  type TRoundIndex,
} from '@ui/index';

import {
  CGamePreviewController,
  readPreviewParamsFromUrl,
  type TPreviewDeps,
} from './game-preview-controller';
import { accountSnapshot, CApiError } from '@net/account-client';
import {
  characterAt,
  CharacterLine,
  pickLine,
  type ICharacter,
  type TCharacterLine,
} from '@core/characters';
import { ZH_TW } from '@i18n/index';
import { CSeatCharacters } from '@ui/seat-characters';
import { showBragPrompt } from '@ui/brag-prompt';
import { maybeShowFirstPlayGuide } from '@ui/first-play-guide';
import { createHttpRound } from '@net/http-round';
import { isRemoteRound } from '@net/remote-round';
import { isServerTableEnabled } from '@net/server-mode';
import { showConnectionError } from '@ui/connection-error';
import { CAICoordinator } from './ai-coordinator';
import { addDealerRelationToResultSummary } from './dealer-relation-summary';
import { CPlayerSelectionController } from './player-selection-controller';
import { createGameSceneLifecycleState } from './game-scene-lifecycle';
import { buildResultChips, buildResultFlowData, type TResultChip } from './result-flow-data';
import { SceneKey } from './scene-keys';
import { createHandViewForSeat, SEAT_LAYOUT } from './seat-layout';
import { buildTenpaiAutoTurnAction } from './tenpai-auto-turn';

// ========================================================================
// 版面常數
// ========================================================================
//
// 邏輯解析度 1280×720。
// SEAT_LAYOUT（4 家手牌 / 副露版面）與 createHandViewForSeat（per-seat 對譯層）
// 已抽到 ./seat-layout.ts，本檔只保留中央捨牌池 / info bar / 風位徽章 / 預覽
// 模式等 Scene 自身要用的版面常數。

// 海底牌池中心座標常數已遷移至 src/ui/discard-pool.ts（CDiscardPool 內部）

// 本家出牌：手牌「滑動補位」時長（與升起放大牌 fade out / 海底 fade in 同時，建議相近）
// ---- AI 牌友鬥嘴 ----
const GREET_DELAY_MS = 900; // 發牌後打招呼
const GREET_LATER_CHANCE = 0.3; // 第二手以後打招呼的機率
const REMEMBER_DAYS = 3; // 連續來或沒來幾天以上才提
const BIG_HAND_MENTION_CHANCE = 0.4; // 提起上次大牌的機率
const MELD_CHATTER_CHANCE = 0.45; // 吃碰槓時講話的機率
const END_CHATTER_DELAY_MS = 1200; // 胡牌／流局後第二句的延遲
const BIG_HAND_TAI = 8; // 本家胡到幾台以上跳出「傳給親友看」
const BRAG_DELAY_MS = 3500; // 等胡牌儀式播完再跳

const HAND_COLLAPSE_MS = 250;
const HAND_DISCARD_MERGE_MS = 250; // 打出原手牌後，補缺口、騰插槽、摸牌滑入同步完成

// ---- 本家摸牌「併入手牌」動畫（摸到沒打出去 → 從分離處插入排序位）----
const DRAW_SHIFT_MS = 160; // 插入點右方牌組往右挪一格的時長
const DRAW_FLY_MS = 240; // 摸進的牌移動落到插入點的時長
const DRAW_DEMO_HOLD_MS = 700; // 預覽 D 鍵：摸的牌分離顯示停留多久才示範併入
const PEEK_LOOP_GAP_MS = 900; // 預覽 G 鍵：瞇牌循環兩輪之間的間隔（等上一輪淡出收場）
const PEEK_FALLBACK_X = LOGICAL_WIDTH / 2; // 瞇牌找不到牌位時的 fallback 中心 X
const PEEK_FALLBACK_Y = LOGICAL_HEIGHT - 70; // 瞇牌找不到牌位時的 fallback Y（畫面底部上方）

// ---- 開局儀式（原版本家 16 張手牌由中央向兩側分四批亮相）----
// 補花動畫各拍時長（單張花：飛向花牌區 → 落定停留 → 替補淡入 → 下一張間隔）
const FLOWER_FLY_MS = 300; // 花從手牌飛向花牌區時長
const FLOWER_LAND_HOLD_MS = 140; // 花落定後、補牌前的停留
const FLOWER_DRAW_MS = 180; // 替補牌淡入時長
const FLOWER_STEP_GAP_MS = 110; // 兩張花（含換家）之間的間隔

// ---- Chrome DevTools 牌局追蹤 ----
const ROUND_TRACE_PREFIX = '[MahjongKing][RoundTrace]'; // Console filter / 複製時使用的固定前綴

// ============================================================================
// 其他版面常數（顏色 / 字體 / 位置；所有 inline 微調都改這裡）
// ============================================================================
// 剩牌計數常數已遷移至 src/ui/wall-counter.ts。

// ---- 中央桌面裝飾文字 (局數 / logo) ----
// ---- 中央資訊面板 (CTableInfo) ----
const TABLE_INFO_Y_OFFSET_FROM_MID = -30; // CTableInfo 中心相對畫面中央的 Y 偏移
const TABLE_INFO_BASE_POINTS = 100; // 預設底分（"X底"）— MVP 寫死
const TABLE_INFO_FAN_POINTS = 20; // 預設台分（"X台"）— MVP 寫死

// ---- 風位徽章 + 莊家標 ----
// 常數與所有渲染細節已遷移至 src/ui/seat-badges.ts（CSeatBadges）

// 玩家選牌放大預覽常數已遷移至 src/scenes/player-selection-controller.ts。

// ========================================================================
// CGameScene
// ========================================================================

export class CGameScene extends Phaser.Scene {
  // --- Core ---
  private bus!: IEventBus<TRoundEventMap>;
  private round!: IRoundDriver;
  private audio!: CAudioManager;
  private ai!: CAICoordinator;
  private playerSeat: TSeat = 0;
  private activeSession: IGameSetSession | null = null;
  private roundPlan: IRoundPlan | null = null;

  // --- UI 組件 ---
  private handViews!: [CHandView, CHandView, CHandView, CHandView];
  private discardPool!: CDiscardPool;
  private actionBar!: CActionBar;
  private actionIcon!: CActionIcon;
  private discardAnnounce!: CDiscardAnnounce;
  // 別家捨牌「門前預告」開關（預設開；預覽模式可切換顯示與否）
  private discardAnnounceEnabled = true;
  private tableInfo!: CTableInfo;
  private wallCounter!: CWallCounter;

  // --- 玩家選牌互動（點手牌 → 上方放大預覽 → 點預覽確認出牌） ---
  private selection!: CPlayerSelectionController;

  // --- 追蹤事件中的最近狀態 ---
  private lastDrawnPerSeat: Array<TTile | null> = [null, null, null, null];
  // 各家「摸到、尚未決定去留」的牌：在手牌成長端分離顯示；出牌 / 併入後清為 null
  private pendingDrawnPerSeat: Array<TTile | null> = [null, null, null, null];
  private lastDiscarded: { tile: TTile; fromSeat: TSeat } | null = null;
  private pendingAddedKong: { tile: TTile; fromSeat: TSeat } | null = null;
  // 海底捨牌「時間排序序列」— 只在 discard.committed（確認無人取用）時 push
  // 餵給 CDiscardPool.update，讓等待反應的牌先停在出牌者門前、不提前進海底
  private discardSequence: TTile[] = [];

  // --- 倒數計時 ---
  private turnTimer!: CTurnTimer;
  private playerStatus!: CPlayerStatus;
  private pausedGameTweens: Phaser.Tweens.Tween[] = [];
  private pauseDialog!: CPauseDialog;
  private gamePaused = false;
  private timerSettingsChangedWhilePaused = false;
  private keyboardHandIndex = 0;
  private keyboardFocusOnActionBar = false;

  // AI 出手排程與整局代打由 CAICoordinator 管理（見 ai-coordinator.ts）。

  // --- 玩家當下是否有可執行的 action（決定 action bar 顯隱）---
  private playerActionPending = false;

  // --- Chrome Console 牌局追蹤流水號（每次 scene create 從 0 開始） ---
  private roundTraceSequence = 0;

  // --- 已結束就不再響應 ---
  private roundEnded = false;

  // --- 風位徽章 + 莊家 icon + 自家 label（整組由 CSeatBadges 統一管理） ---
  private seatBadges!: CSeatBadges;

  // --- 回合提示（指示手透明，改由該家門前風字 active 樣式顯示） ---
  private turnHand!: CTurnIndicatorHand;
  // 目前以 active 樣式亮起的門前風字；null = 動作／補花／落牌期間不提示任何一家。
  private activeTurnSeat: TSeat | null = null;
  // 捨牌進海底動畫完成前，暫存核心已同步發布的下一回合 action.available
  private readonly discardLandingGate = new CDiscardLandingGate<
    TRoundEventMap['action.available'],
    TRoundEventMap['tile.drawn']
  >();
  // 對局途中補花：花圖示 fade out 完成前，暫存該家的回合 action（含手勢 / 玩家 UI / AI）。
  private readonly flowerTurnGate = new CFlowerTurnGate<TRoundEventMap['action.available']>();
  // 吃碰槓字樣播完、副露手牌落定前，暫存該家的下一個出牌決策。
  private readonly meldTurnGate = new CMeldTurnGate<TRoundEventMap['action.available']>();
  // 起手暗槓可能直接切到下一家；所有跨座位 action 都需等目前的槓／花圖示播完再放行。
  private pendingActionAfterIcons: TRoundEventMap['action.available'] | null = null;
  // 吃／碰／槓後該家仍須出牌，但此段不顯示回合手勢；打出牌後解除
  private turnHandSuppressedUntilDiscardSeat: TSeat | null = null;

  // --- 補花橫幅（補花階段中央 overlay：花帶 + 補花徽章） ---
  private flowerBanner!: CFlowerBanner;

  // --- 聽牌模式 ---
  private tenpaiPeek!: CTenpaiPeek; // 瞇牌動畫（摸牌後手掌+拇指掀牌）
  private tenpaiBanner!: CTenpaiBanner; // 宣告「聽」的橫幅飛入動畫
  private winCeremony!: CWinCeremony; // 自摸／胡／放槍動畫 + 完成後點擊繼續
  private gameDrawCeremony!: CGameDrawCeremony; // 流局波浪／印記 + 完成後點擊繼續
  // 出牌後偵測到聽牌 → 彈出「聽 / 放棄」action bar，等玩家決定；期間暫停 AI 排程
  private tenpaiOfferActive = false;
  // 被 tenpaiOfferActive 暫停、待玩家決定後才放行的 AI 座位（action.available 收集）
  private gatedAISeats: TSeat[] = [];
  // 聽牌自動回合的瞇牌動畫進行中：期間玩家不能手選、回合手勢隱藏
  private animatingTenpaiPeek = false;
  // 預覽模式聽牌 demo：顯示「聽」圖示的座位（真實模式改讀 round.declaredTenpai）
  private previewTenpaiSeats = new Set<TSeat>();
  // 預覽模式 G：瞇牌動畫循環播放中（調校手掌 / 拇指座標用）
  private peekLoopActive = false;

  // --- 預覽模式（?preview=1）：靜態畫面，不啟動 CRound、AI 不動 ---
  // null = 真實模式；非 null = 預覽模式，所有 mock 狀態 / dealerSeat 由 controller 持有
  private preview: CGamePreviewController | null = null;

  // --- 逐步模式（預覽模式按 S 進入）：真實 CRound + 四家全 AI，按 N 一手手推進 ---
  // 攔截 action.available 的自動排程，改由 stepOnce() 手動驅動；玩家不操作、不能選牌
  private stepMode = false;

  // --- 靜態預覽：active 風字目前展示在哪一家（Preview 層按 N 循環切換） ---
  private previewHandSeat: TSeat = 0;

  // --- 本家出牌補位動畫進行中：期間略過手牌重建，交給 collapseGap 動畫負責視覺 ---
  private animatingPlayerDiscard = false;

  // --- 本家摸牌整理動畫進行中：期間略過手牌重建、且不能選牌，交給 animateDraw 驅動 ---
  private animatingPlayerDraw = false;

  // --- 補花動畫進行中：期間 tile.drawn 不自動重建手牌（交給 animateFlowerReplacement 驅動）---
  // 預覽 F 補花時玩家手牌讀真實狀態靠 previewFlowerPhase 已設的 stepMode 旗標（見 getCurrentPlayerHand）
  private animatingFlower = false;

  // --- 伺服器牌局：每次開局遞增；建局回來時若已不同（離開或重開）就丟棄結果 ---
  private serverRoundEpoch = 0;

  // --- 三位 AI 牌友的頭像與對話泡泡（每次進場重建） ---
  private seatCharacters: CSeatCharacters | null = null;

  constructor() {
    super({ key: SceneKey.Game });
  }

  create(): void {
    this.resetSceneLifecycleState();
    this.pausedGameTweens = [];
    // 預覽模式判定：URL 含 ?preview=1 時略過 CRound.start，渲染靜態 mock 畫面
    // 用途：UAT 視覺驗收、Playwright 截圖比對
    const previewParams = readPreviewParamsFromUrl();

    this.setupCore();
    this.setupUI();

    if (previewParams.enabled) {
      // 在 subscribeEvents 之前先建 controller，讓 renderSeatBadges 能讀到 preview 狀態
      this.preview = new CGamePreviewController(
        previewParams.activeSeat,
        this.buildPreviewDeps(),
        previewParams.dealerSeat,
        previewParams.openingSeat
      );
      this.previewHandSeat = previewParams.activeSeat;
      // 之前 setupUI 內已用「無 preview」狀態渲染過徽章，這裡重畫一次套用預覽座風
      this.renderSeatBadges();
    }

    this.subscribeRoundTrace();
    this.subscribeEvents();
    if (!this.preview) this.setupCharacters();
    this.traceRound('scene.ready', { preview: this.preview !== null });
    this.audio = new CAudioManager(this, this.bus);
    this.events.on(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.serverRoundEpoch++; // 建局中途離開：回來的結果不再套用
      this.seatCharacters?.destroy();
      this.seatCharacters = null;
      this.audio.destroy();
      this.ai.destroy();
      this.turnTimer?.destroy();
      this.playerStatus?.destroy();
      this.pauseDialog?.destroy();
      this.winCeremony?.destroy();
      this.gameDrawCeremony?.destroy();
    });

    if (this.preview) {
      this.preview.bindKeyboard();
      this.preview.populate();
      // 靜態預覽以 active 風字展示回合（預設本家，按 N 可循環切換到下一家）
      this.showTurnIndicator(this.previewHandSeat);
    } else {
      this.bindPauseKeyboard();
      this.audio.playGameStart();
      this.startRound();
      maybeShowFirstPlayGuide(loadSettings().doubleTapDiscard);
    }
  }

  /**
   * AI 牌友：頭像、名字，並在吃碰、胡牌、放槍、流局時鬥嘴。
   * 每將第一手開局時打招呼，會記得玩家連續來了幾天、好幾天沒來、上次胡的大牌。
   */
  private setupCharacters(): void {
    const tableId = this.activeSession?.tableAI?.tableId;
    const seats = new Map<TSeat, ICharacter>();
    for (const seat of [Seat.Right, Seat.Opposite, Seat.Left] as const) {
      const character = characterAt(tableId, seat);
      if (character) seats.set(seat, character);
    }
    if (seats.size === 0) return;
    const cast = new CSeatCharacters(this, seats);
    this.seatCharacters = cast;
    const speak = (seat: TSeat, line: TCharacterLine, vars = {}, delay = 0): void => {
      const character = cast.character(seat);
      if (!character) return;
      const text = pickLine(character, line, Math.random, vars);
      if (delay > 0) this.time.delayedCall(delay, () => cast.say(seat, text));
      else cast.say(seat, text);
    };
    const randomAISeat = (): TSeat =>
      ([...seats.keys()][Math.floor(Math.random() * seats.size)] ?? Seat.Right) as TSeat;
    this.bus.on('wall.dealt', () => {
      if ((this.activeSession?.completedRounds ?? 0) > 0 && Math.random() > GREET_LATER_CHANCE)
        return;
      const account = accountSnapshot();
      const best = account?.stats.bestHand;
      const bestName = best?.items.length
        ? (ZH_TW.scoring as Record<string, string>)[best.items[0]!]
        : undefined;
      const seat = randomAISeat();
      if (account && account.visitStreak >= REMEMBER_DAYS)
        speak(seat, CharacterLine.GreetStreak, { days: account.visitStreak }, GREET_DELAY_MS);
      else if (account && account.daysAway >= REMEMBER_DAYS)
        speak(seat, CharacterLine.GreetReturn, { days: account.daysAway }, GREET_DELAY_MS);
      else if (bestName && Math.random() < BIG_HAND_MENTION_CHANCE)
        speak(seat, CharacterLine.GreetBigHand, { hand: bestName }, GREET_DELAY_MS);
      else speak(seat, CharacterLine.Greet, { name: '你' }, GREET_DELAY_MS);
    });
    this.bus.on('meld.formed', ({ seat }) => {
      if (seat !== this.playerSeat && Math.random() < MELD_CHATTER_CHANCE)
        speak(seat, CharacterLine.Meld);
    });
    this.bus.on('round.ended', ({ summary }) => {
      if (summary.kind !== RoundSummaryKind.Hu) {
        speak(randomAISeat(), CharacterLine.Draw, {}, END_CHATTER_DELAY_MS);
        return;
      }
      const result = summary.results[0];
      if (!result) return;
      if (result.seat === this.playerSeat) {
        speak(randomAISeat(), CharacterLine.PlayerWin, {}, END_CHATTER_DELAY_MS);
        // 胡大牌：跳出「傳給親友看」（只炫耀，不附購買連結）
        const items = result.scoreItems.filter(
          (item) => item.key !== 'dealer' && item.key !== 'lianzhuang'
        );
        const tai = items.reduce((sum, item) => sum + item.points, 0);
        if (tai >= BIG_HAND_TAI)
          this.time.delayedCall(BRAG_DELAY_MS, () =>
            showBragPrompt(
              [...items]
                .sort((a, b) => b.points - a.points)
                .slice(0, 3)
                .map((item) => (ZH_TW.scoring as Record<string, string>)[item.key] ?? item.key),
              tai
            )
          );
        return;
      }
      speak(result.seat, result.isSelfDraw ? CharacterLine.SelfDraw : CharacterLine.Win);
      if (result.loserSeat !== null && result.loserSeat !== this.playerSeat)
        speak(result.loserSeat, CharacterLine.DealIn, {}, END_CHATTER_DELAY_MS);
    });
  }

  /** 正式對局：開了伺服器牌局就先向伺服器建局並取回發牌結果，否則用本機 CRound。 */
  private startRound(): void {
    if (!isServerTableEnabled() || !this.roundPlan) {
      this.startNewRoundWithCeremony();
      return;
    }
    const epoch = ++this.serverRoundEpoch;
    const failed = (error: unknown): void => {
      console.error('[server round]', error);
      this.ai.pause();
      // 這一手已在伺服器開始過（例如重新整理）：回首頁，續局時伺服器會先代打完上一手
      if (error instanceof CApiError && error.status === 409) {
        this.scene.start(SceneKey.Home);
        return;
      }
      showConnectionError(error instanceof Error ? error.message : String(error));
    };
    createHttpRound({ playerSeat: this.playerSeat, bus: this.bus, onError: failed })
      .then((remote) => {
        if (epoch !== this.serverRoundEpoch || !this.scene.isActive()) return; // 已離開牌局
        this.round = remote;
        this.traceRound('server.round.created', {});
        this.startNewRoundWithCeremony();
      })
      .catch(failed);
  }

  /** 伺服器牌局：結算已在伺服器完成並寫回本機快取，這裡只取結果畫結算畫面。 */
  private applyServerSettlement(): ReadonlyArray<TResultChip> | null {
    const settlement = isRemoteRound(this.round) ? this.round.lastSettlement : null;
    if (!settlement) return null;
    const { deltaBySeat, balancesAfter } = settlement;
    this.traceRound('round.settlement.applied', { server: true, deltaBySeat });
    this.activeSession = loadGameSave().activeSession;
    this.roundPlan = null;
    const before = [0, 1, 2, 3].map((seat) => balancesAfter[seat]! - deltaBySeat[seat]!) as [
      number,
      number,
      number,
      number,
    ];
    return buildResultChips(before, deltaBySeat);
  }

  /** AI 看得到的公開資訊：底台與四家籌碼。 */
  private aiPublicContext(): { base: number; taiUnit: number; balances: number[] } {
    return {
      base: this.roundPlan?.base ?? 100,
      taiUnit: this.roundPlan?.taiUnit ?? 20,
      balances: this.activeSession ? [...loadGameSave().ledger.balances] : [0, 0, 0, 0],
    };
  }

  /**
   * Phaser 的 scene.start 會重用同一個 CGameScene instance，不會重跑欄位初始化式。
   * 因此由結算畫面開始下一局時，必須先清掉上一局的結束旗標與所有動畫／事件暫態。
   */
  private resetSceneLifecycleState(): void {
    const state = createGameSceneLifecycleState();
    this.discardAnnounceEnabled = state.discardAnnounceEnabled;
    this.lastDrawnPerSeat = state.lastDrawnPerSeat;
    this.pendingDrawnPerSeat = state.pendingDrawnPerSeat;
    this.lastDiscarded = state.lastDiscarded;
    this.pendingAddedKong = null;
    this.discardSequence = state.discardSequence;
    this.playerActionPending = state.playerActionPending;
    this.roundTraceSequence = state.roundTraceSequence;
    this.roundEnded = state.roundEnded;
    this.activeTurnSeat = state.activeTurnSeat;
    this.turnHandSuppressedUntilDiscardSeat = state.turnHandSuppressedUntilDiscardSeat;
    this.tenpaiOfferActive = state.tenpaiOfferActive;
    this.gatedAISeats = state.gatedAISeats;
    this.animatingTenpaiPeek = state.animatingTenpaiPeek;
    this.previewTenpaiSeats = state.previewTenpaiSeats;
    this.peekLoopActive = state.peekLoopActive;
    this.preview = null;
    this.stepMode = state.stepMode;
    this.previewHandSeat = state.previewHandSeat;
    this.animatingPlayerDiscard = state.animatingPlayerDiscard;
    this.animatingPlayerDraw = state.animatingPlayerDraw;
    this.animatingFlower = state.animatingFlower;
    this.gamePaused = state.gamePaused;
    this.timerSettingsChangedWhilePaused = false;
    this.keyboardHandIndex = 0;
    this.keyboardFocusOnActionBar = false;
    this.pendingActionAfterIcons = null;
    this.discardLandingGate.cancel();
    this.flowerTurnGate.cancel();
    this.meldTurnGate.cancel();
  }

  /** 把 scene 內各 UI 元件 + 互動 callback 包成 deps 傳給預覽 controller。 */
  private buildPreviewDeps(): TPreviewDeps {
    return {
      scene: this,
      playerSeat: this.playerSeat,
      handViews: this.handViews,
      discardPool: this.discardPool,
      actionBar: this.actionBar,
      actionIcon: this.actionIcon,
      discardAnnounce: this.discardAnnounce,
      wallCounter: this.wallCounter,
      seatBadges: this.seatBadges,
      tableInfo: this.tableInfo,
      updatePlayerHandView: () => this.updatePlayerHandView(),
      setDiscardSequence: (seq) => {
        this.discardSequence = [...seq];
      },
      appendDiscard: (tile) => {
        this.discardSequence.push(tile);
        // 預覽模式玩家出牌也走 fade in（與真實出牌一致）
        this.discardPool.update(this.discardSequence, { fadeInLast: true });
      },
      rerenderSeatBadges: () => this.renderSeatBadges(),
      startTurnTimer: () => this.turnTimer.start(),
      startStepRound: () => this.startStepRound(),
      stepOnce: () => this.stepOnce(),
      exitStepRound: () => this.exitStepRound(),
      cyclePreviewHandSeat: () => this.cyclePreviewHandSeat(),
      previewSelfDraw: (seat, tuning) => this.previewWinCeremony(true, seat, tuning),
      updateSelfDrawPreviewHand: (seat, tuning) => this.revealPreviewSelfDrawHand(seat, tuning),
      cancelSelfDrawPreview: () => {
        this.winCeremony.destroy();
        this.showTurnIndicator(this.previewHandSeat);
      },
      previewDiscardWin: () => this.previewWinCeremony(false),
      previewGameDraw: () => this.previewGameDrawCeremony(),
      previewFlowerPhase: (onDone) => this.previewFlowerPhase(onDone),
      previewPlayerDraw: (onDone) => this.previewPlayerDraw(onDone),
      previewTenpai: (onDone) => this.previewTenpaiCeremony(onDone),
      previewPeekLoop: (on) => this.previewPeekLoop(on),
      setDiscardAnnounceEnabled: (on) => {
        this.discardAnnounceEnabled = on;
      },
    };
  }

  /** Preview 的自摸子選單／H：不跑核心牌局，預覽指定家自摸或一炮多響胡牌儀式。 */
  private previewWinCeremony(
    isSelfDraw: boolean,
    selfDrawSeat: TSeat = this.playerSeat,
    selfDrawTuning?: ISelfDrawHandTuning
  ): void {
    const summary: TRoundSummary = isSelfDraw
      ? {
          kind: RoundSummaryKind.Hu,
          results: [
            {
              seat: selfDrawSeat,
              isSelfDraw: true,
              loserSeat: null,
              winTile: {
                id: 9900 + selfDrawSeat,
                suit: Suit.Man,
                rank: (selfDrawSeat % 9) + 1,
              },
              scoreItems: [],
              total: 4,
            },
          ],
        }
      : {
          kind: RoundSummaryKind.Hu,
          results: [
            {
              seat: 1,
              isSelfDraw: false,
              loserSeat: 3,
              winTile: { id: 9901, suit: Suit.Pin, rank: 5 },
              scoreItems: [],
              total: 3,
            },
            {
              seat: 2,
              isSelfDraw: false,
              loserSeat: 3,
              winTile: { id: 9902, suit: Suit.Pin, rank: 5 },
              scoreItems: [],
              total: 2,
            },
          ],
        };
    const plan = buildWinCeremonyPlan(summary);
    if (!plan) return;

    this.hideTurnIndicator();
    this.winCeremony.play(plan, {
      playWinSound: (selfDraw) => this.audio.playWinCeremony(selfDraw),
      onRevealWinners: () => {
        if (!isSelfDraw) return;
        const result = summary.kind === RoundSummaryKind.Hu ? summary.results[0] : undefined;
        if (!result) return;
        this.revealPreviewSelfDrawHand(result.seat, selfDrawTuning, result.winTile);
      },
      onContinue: () => {
        if (this.preview) {
          this.preview.restoreMockState();
          this.showTurnIndicator(this.previewHandSeat);
        }
      },
    });
  }

  /** 自摸預覽：以指定調校值重畫該家推倒手牌，不重播全畫面特效。 */
  private revealPreviewSelfDrawHand(
    seat: TSeat,
    tuning?: ISelfDrawHandTuning,
    winTile: TTile = {
      id: 9900 + seat,
      suit: Suit.Man,
      rank: (seat % 9) + 1,
    }
  ): void {
    const mockHand = this.preview?.getMockHand(seat);
    if (!mockHand) return;
    const selfDrawHand = addToConcealed(mockHand, winTile);
    this.handViews[seat].update(selfDrawHand, undefined, {
      drawnTileId: winTile.id,
      selfDrawReveal: true,
      ...(tuning ? { selfDrawTuning: tuning } : {}),
    });
  }

  /** Preview 層 L：直接預覽原版流局波浪與印記，不啟動核心牌局。 */
  private previewGameDrawCeremony(): void {
    this.hideTurnIndicator();
    this.gameDrawCeremony.play(() => {
      if (this.preview) this.showTurnIndicator(this.previewHandSeat);
    });
  }

  /**
   * Preview 層按 F：預覽補花動畫。
   * 反覆重置（重發）各家手牌，直到某一家有花，才開始跑補花替換動畫；跑完還原靜態 mock（onDone）。
   * 期間借用 stepMode 旗標讓 getCurrentPlayerHand 改讀真實 round 狀態。
   */
  private previewFlowerPhase(onDone: () => void): void {
    this.stepMode = true;
    this.selection.clear();
    this.actionBar.setVisible(false);
    this.stopTimer();

    // 重置各家手牌，直到至少一家有花（保證補花動畫看得到）；cap 防極端情況死迴圈
    let attempts = 0;
    do {
      this.rebuildRoundAllAI();
      this.round.dealStaged(); // 發牌（含花）→ wall.dealt 渲染各家手牌
      attempts++;
    } while (!this.round.hasUnreplacedFlowers() && attempts < 30);

    this.runFlowerPhase(() => {
      // 補完不開打：還原回靜態 mock
      this.stepMode = false;
      onDone();
      this.showTurnIndicator(this.previewHandSeat);
    });
  }

  /**
   * Preview 層按 D：預覽本家摸牌整理動畫。
   * 合成一張會插在手牌中段的牌（筒5），跑「摸進 → 看一下 → 插入整理」動畫；
   * 跑完呼叫 onDone（由 controller 還原靜態 mock）。
   */
  private previewPlayerDraw(onDone: () => void): void {
    const mock = this.preview?.getMockHand();
    if (!mock) {
      onDone();
      return;
    }
    this.animatingPlayerDraw = true;
    this.selection.clear();
    this.hideTurnIndicator(); // 摸牌過程不顯示回合提示（結束才亮起風字）
    const drawn: TTile = { id: 9000, suit: Suit.Pin, rank: 5 }; // 合成摸進的牌（插在筒3、筒7 之間）
    const afterHand = addToConcealed(mock, drawn);
    // 1. 先「分離顯示」摸進的牌在手牌右方
    this.handViews[this.playerSeat].update(afterHand, undefined, {
      drawnTileId: drawn.id,
    });
    // 2. 停留後示範「沒打出去 → 併入手牌」的插入動畫
    this.time.delayedCall(DRAW_DEMO_HOLD_MS, () => {
      this.handViews[this.playerSeat].animateDrawnMerge(
        afterHand,
        drawn,
        { holdMs: 0, shiftMs: DRAW_SHIFT_MS, flyMs: DRAW_FLY_MS },
        () => {
          this.animatingPlayerDraw = false;
          onDone();
          // 併入結束 → 回合手勢淡入（還原到目前預覽展示的那一家）
          this.showTurnIndicator(this.previewHandSeat, { fadeIn: true });
        }
      );
    });
  }

  /** Preview 層按 N：active 風字切換到下一家（本家→下家→對家→上家→…循環）。 */
  private cyclePreviewHandSeat(): void {
    this.previewHandSeat = ((this.previewHandSeat + 1) % 4) as TSeat;
    this.showTurnIndicator(this.previewHandSeat);
  }

  // ----------------------------------------------------------------------
  // 開局儀式：擲骰場景切入 → 完整顯示手牌 → 補花 → 開始出牌
  // ----------------------------------------------------------------------

  /**
   * 正式對局開局流程（擲骰／風局已由 CDiceScene 播完）：
   *   1. dealStaged()：發牌（含花）→ 四家手牌直接完整顯示
   *   2. 補花：各家輪流把手上的花依序替換（依序摸牌），有動畫節奏
   *   3. beginPlay()：莊家開始第一個 turn
   */
  private startNewRoundWithCeremony(): void {
    this.round.dealStaged();
    this.runFlowerPhase(() => {
      if (!this.roundEnded) this.round.beginPlay();
    });
  }

  /**
   * 補花階段入口：先判斷有無待補的花。
   *   - 無花 → 不顯示橫幅、不做動畫，直接 onDone（維持原流程）
   *   - 有花 → 中央顯示「補花」橫幅；進場完成後逐張補花，全部補完淡出橫幅再 onDone
   * 觸發時機由 caller（startNewRoundWithCeremony / previewFlowerPhase）保證在「莊家開打前」。
   */
  private runFlowerPhase(onDone: () => void): void {
    if (this.roundEnded || !this.round.hasUnreplacedFlowers()) {
      onDone();
      return;
    }
    this.hideTurnIndicator(); // 補花橫幅出現起即取消高亮；補完後由 action.available 或預覽還原
    this.flowerBanner.show(() => {
      this.runFlowerReplacements(() => {
        this.flowerBanner.hide(() => onDone());
      });
    });
  }

  /**
   * 逐張補花動畫（各家輪流把手上的花替換）。
   * 補花順序與 replaceNextFlower 一致（seat 0→3，同家補完再換下家）；全補完後 onDone。
   *
   * 流程（每張花）：
   *   1. 從 round 狀態找下一張待補的花（決定 seat / flower）— 必須在 replaceNextFlower 前，
   *      才能拿到花在手牌中的原位（此時 view 仍顯示補花前狀態）
   *   2. round.replaceNextFlower() 推進核心狀態（移花 + 摸替補）
   *   3. handViews[seat].animateFlowerReplacement(...) 跑三拍動畫
   *   4. 動畫完成 → 間隔 FLOWER_STEP_GAP_MS → 補下一張
   */
  private runFlowerReplacements(onDone: () => void): void {
    this.animatingFlower = true;
    const finish = (): void => {
      this.animatingFlower = false;
      onDone();
    };
    const step = (): void => {
      if (this.roundEnded) {
        this.animatingFlower = false;
        return;
      }
      // 找下一張待補的花（seat 0→3，與 replaceNextFlower 同序）
      const hands = this.round.getState().hands;
      let seat: TSeat | null = null;
      let flower: TTile | null = null;
      for (let s = 0; s < 4; s++) {
        const f = hands[s as TSeat].concealed.find((t) => isFlower(t));
        if (f) {
          seat = s as TSeat;
          flower = f;
          break;
        }
      }
      if (seat === null || flower === null) {
        finish(); // 各家都沒花了 → 補花完成
        return;
      }

      // 此時 view 仍顯示補花前狀態（花還在手牌）→ animateFlowerReplacement 可定位花的原位
      const result = this.round.replaceNextFlower();
      if (!result) {
        // 補替補時摸到海底 → endRoundDraw（roundEnded 由事件設定）
        this.animatingFlower = false;
        return;
      }
      const afterHand = this.round.getState().hands[seat];
      this.handViews[seat].animateFlowerReplacement(
        afterHand,
        result.flower,
        result.replacement,
        {
          flyMs: FLOWER_FLY_MS,
          holdMs: FLOWER_LAND_HOLD_MS,
          drawMs: FLOWER_DRAW_MS,
        },
        () => this.time.delayedCall(FLOWER_STEP_GAP_MS, step)
      );
    };
    step();
  }

  // ----------------------------------------------------------------------
  // 逐步模式（預覽模式按 S 進入、按 N 推進）
  // ----------------------------------------------------------------------

  /**
   * 進入逐步模式（預覽模式按 S）：重建一局全新 CRound（四家全 AI）並發牌。
   * round.start() 會讓莊家摸進第一張，停在莊家 turn 的 action.available
   * （被 stepMode 攔截、不自動出手），等玩家按 N。
   * 重建而非沿用既有 round：支援「ESC 退回 Preview 後再按 S」開新局（CRound.start 只在 Init 階段有效）。
   */
  private startStepRound(): void {
    this.stepMode = true;
    this.selection.clear();
    this.actionBar.setVisible(false);
    this.stopTimer();
    this.resetTenpaiState();
    this.rebuildRoundAllAI();
    this.round.start();
  }

  /** 清掉聽牌相關暫態（換局 / 進出逐步模式時）。 */
  private resetTenpaiState(): void {
    this.tenpaiOfferActive = false;
    this.gatedAISeats = [];
    this.animatingTenpaiPeek = false;
    this.tenpaiPeek.destroy();
    this.tenpaiBanner.destroy();
    this.previewTenpaiSeats.clear();
    this.actionIcon.setTenpaiSeats(new Set()); // 清掉各家「聽」持續標
  }

  /** Game 層按 ESC 退回 Preview：關閉逐步狀態（畫面由 controller.restoreMockState 覆寫回 mock）。 */
  private exitStepRound(): void {
    this.stepMode = false;
    this.roundEnded = false;
    this.stopTimer();
    this.actionBar.setVisible(false);
    this.resetTenpaiState();
    // 回到靜態預覽：回合手勢復位到目前預覽展示的那一家
    this.showTurnIndicator(this.previewHandSeat);
  }

  /** 標準開局設定（莊家 0、東風局、四門風固定）；playerSeat 指定玩家佔位座位。 */
  private makeRoundConfig(playerSeat: TSeat): TRoundConfig {
    if (this.roundPlan) {
      return {
        dealerSeat: this.roundPlan.dealerSeat,
        dealerStreak: this.roundPlan.dealerStreak,
        prevailingWind: this.roundPlan.prevailingWind,
        seatWinds: this.roundPlan.seatWinds,
        playerSeat,
        openingSeat: this.roundPlan.openingSeat,
        openingStackIndex: this.roundPlan.openingStackIndex,
        wallStartStackIndex: this.roundPlan.wallStartStackIndex,
      };
    }
    return {
      dealerSeat: 0,
      prevailingWind: Wind.East,
      seatWinds: [Wind.East, Wind.South, Wind.West, Wind.North],
      playerSeat,
    };
  }

  /** 重建一局全新 CRound（莊家 0、四家全 AI），沿用同一條 event bus。 */
  private rebuildRoundAllAI(): void {
    const seed = Date.now() ^ Math.floor(Math.random() * 1e9);
    this.round = new CRound(this.makeRoundConfig(this.playerSeat), createSeedRng(seed), this.bus);
    this.ai.configure(createTableAIProfile(), null, `preview/${Date.now()}`);
    this.lastDrawnPerSeat = [null, null, null, null];
    this.pendingDrawnPerSeat = [null, null, null, null];
    this.lastDiscarded = null;
    this.pendingAddedKong = null;
    this.roundEnded = false;
  }

  /**
   * 按 N 推進一手：
   * 1. 當前 turn 家由 AI 出手（出牌 / 暗槓 / 加槓 / 自摸胡）
   * 2. 若引發捨牌反應，CRound 依優先序逐家詢問，吃 / 碰 / 槓 / 胡 / 過由該家 AI 決定，
   *    有人接受便落定、放棄才問下一家，最後停在下一家（或副露家）的 turn
   * 槓 / 副露後該家仍需出牌 → 留待下一次按 N，維持「一次 N = 一個出牌決策」。
   */
  private stepOnce(): void {
    if (this.roundEnded) return;
    if (
      this.discardLandingGate.isWaiting() ||
      this.flowerTurnGate.isWaiting() ||
      this.meldTurnGate.isWaiting()
    )
      return;
    if (
      this.round.getPhase() === RoundPhase.Turn ||
      this.round.getPhase() === RoundPhase.InitialKong
    ) {
      this.ai.execute(this.round.getState().currentSeat);
    }
    // 結算捨牌反應，直到回到某家 turn 或牌局結束
    let guard = 0;
    while (!this.roundEnded && this.round.getPhase() === RoundPhase.WaitingReaction) {
      const state = this.round.getState();
      const pending = [...state.pendingReactionSeats].filter(
        (s) => !state.receivedReactions.has(s)
      );
      for (const s of pending) {
        if (this.roundEnded || this.round.getPhase() !== RoundPhase.WaitingReaction) break;
        this.ai.execute(s);
      }
      if (++guard > 8) break; // 安全閥：理論上 resolve 後一定離開 WaitingReaction
    }
  }

  // ----------------------------------------------------------------------
  // 初始化
  // ----------------------------------------------------------------------

  private setupCore(): void {
    const isPreview = readPreviewParamsFromUrl().enabled;
    if (!isPreview) {
      this.activeSession = loadGameSave().activeSession;
      this.roundPlan = this.activeSession?.currentRound ?? null;
      this.playerSeat = this.activeSession?.playerSeat ?? 0;
    } else {
      this.activeSession = null;
      this.roundPlan = null;
    }
    const seed = this.roundPlan?.wallSeed ?? Date.now() ^ Math.floor(Math.random() * 1e9);
    this.bus = createEventBus<TRoundEventMap>();
    this.round = new CRound(this.makeRoundConfig(this.playerSeat), createSeedRng(seed), this.bus);
    this.ai = new CAICoordinator({
      scene: this,
      playerSeat: this.playerSeat,
      getRound: () => this.round,
      publicContext: this.aiPublicContext(),
      isRoundEnded: () => this.roundEnded,
      getLastDiscarded: () => this.lastDiscarded,
      onProxyHandoff: () => this.proxyHandoffCleanup(),
      onThinkingChange: (seat) => {
        if (!this.playerStatus) return;
        if (seat === null) this.playerStatus.hideThinking();
        else this.playerStatus.showThinking(seat);
      },
      onPermanentProxyChange: (active) => {
        this.playerStatus?.setProxyActive(active);
        this.configureActionBarPersistentControls();
        this.refreshActionBarVisibility();
      },
    });
    this.ai.configure(
      this.activeSession?.tableAI ?? createTableAIProfile(),
      this.playerSeat,
      `${this.activeSession?.sessionId ?? 'preview'}/${this.roundPlan?.roundId ?? 'round'}`
    );
  }

  /** 代打交接時的 UI 清理（由 CAICoordinator 於 beginHandoff 回呼）。 */
  private proxyHandoffCleanup(): void {
    this.playerActionPending = false;
    this.stopTimer();
    this.actionBar.clear();
    this.configureActionBarPersistentControls();
    this.refreshActionBarVisibility();
    this.selection.clear(); // 取消選取（順便清掉放大預覽）
    this.updatePlayerHandView(); // canPlayerSelectTile=false → 邊框關閉
  }

  /** 每次 actionBar.clear() 後重新掛回常駐控制；整局代打後只保留離開。 */
  private configureActionBarPersistentControls(): void {
    if (!this.actionBar) return;
    this.actionBar.setHandler(ActionKey.Exit, () => this.pauseGame());
    const canEnableProxy = !this.ai.isPermanentProxy();
    this.actionBar.setHandler(
      ActionKey.AiProxy,
      canEnableProxy ? () => this.ai.enablePermanentProxy() : undefined
    );
    this.actionBar.setPersistentVisible(ActionKey.Exit, true);
    this.actionBar.setPersistentVisible(ActionKey.AiProxy, canEnableProxy);
  }

  private pauseGame(): void {
    if (this.preview || this.roundEnded || this.gamePaused) return;
    this.gamePaused = true;
    this.audio.pauseBGM();
    this.turnTimer.pause();
    this.ai.pause();
    this.pausedGameTweens = this.tweens.getTweens().filter((tween) => !tween.paused);
    this.pausedGameTweens.forEach((tween) => tween.pause());
    this.pauseDialog.show();
    this.refreshActionBarVisibility();
    this.time.paused = true;
  }

  private resumeGame(): void {
    if (!this.gamePaused) return;
    this.pauseDialog.hide();
    this.gamePaused = false;
    this.audio.resumeTableBGM();
    this.time.paused = false;
    this.pausedGameTweens.forEach((tween) => tween.resume());
    this.pausedGameTweens = [];
    this.ai.resume();
    if (this.timerSettingsChangedWhilePaused) {
      this.timerSettingsChangedWhilePaused = false;
      if (this.playerActionPending && !this.ai.isProxyActive()) this.startTimer();
    } else {
      this.turnTimer.resume();
    }
    this.refreshActionBarVisibility();
    this.updatePlayerHandView();
  }

  /** 保留 activeSession/currentRound；首頁的繼續流程會用同一 seed 從本局開頭重建。 */
  private leaveRoundForHome(): void {
    this.pauseDialog.hide();
    this.gamePaused = false;
    this.time.paused = false;
    this.tweens.resumeAll();
    this.turnTimer.stop();
    this.ai.destroy();
    this.scene.start(SceneKey.Home);
  }

  private bindPauseKeyboard(): void {
    const handleKeyboard = (event: KeyboardEvent): void => {
      if (this.pauseDialog.handleKeyboard(event)) {
        event.preventDefault();
        return;
      }
      if (event.key === 'Escape' || event.key === 'Backspace') {
        this.pauseGame();
        event.preventDefault();
        return;
      }
      if (event.key === 'ArrowUp' && this.actionBar.isVisible()) {
        this.keyboardFocusOnActionBar = true;
        this.actionBar.focusKeyboard();
        this.updatePlayerHandView();
        event.preventDefault();
        return;
      }
      if (event.key === 'ArrowDown' && this.canPlayerSelectTile()) {
        this.keyboardFocusOnActionBar = false;
        this.actionBar.clearKeyboardFocus();
        this.updatePlayerHandView();
        event.preventDefault();
        return;
      }
      if (this.keyboardFocusOnActionBar && this.actionBar.handleKeyboard(event)) {
        event.preventDefault();
        return;
      }
      if (!this.canPlayerSelectTile()) return;
      const tiles = this.getCurrentPlayerHand().concealed;
      if (tiles.length === 0) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        const delta = event.key === 'ArrowLeft' ? -1 : 1;
        this.keyboardHandIndex =
          (Math.min(this.keyboardHandIndex, tiles.length - 1) + delta + tiles.length) %
          tiles.length;
        this.updatePlayerHandView();
        event.preventDefault();
        return;
      }
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        const tile = tiles[Math.min(this.keyboardHandIndex, tiles.length - 1)];
        if (tile) this.selection.handleTileClick(tile);
        event.preventDefault();
      }
    };
    window.addEventListener('keydown', handleKeyboard);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      window.removeEventListener('keydown', handleKeyboard);
    });
  }

  private setupUI(): void {
    createTableBackground(this);
    // 左下剩牌計數
    this.wallCounter = new CWallCounter(this);
    this.createCenterDecorations();

    const handClick = (tile: TTile): void => {
      if (this.selection.getSelectedTile()) return;
      this.traceRound('ui.hand.click', { tile });
      this.selection.handleTileClick(tile);
    };
    this.handViews = [
      createHandViewForSeat(this, 0, this.playerSeat, handClick),
      createHandViewForSeat(this, 1, this.playerSeat, handClick),
      createHandViewForSeat(this, 2, this.playerSeat, handClick),
      createHandViewForSeat(this, 3, this.playerSeat, handClick),
    ] as [CHandView, CHandView, CHandView, CHandView];

    this.selection = new CPlayerSelectionController({
      scene: this,
      playerSeat: this.playerSeat,
      handViews: this.handViews,
      canSelect: () => this.canPlayerSelectTile(),
      // 牌選好的瞬間：回合手勢淡出消失，接著才是升起 → 打到海底的動畫
      onPickTile: () => this.fadeOutTurnIndicator(),
      onSelectionChange: () => {
        this.updatePlayerHandView();
        this.refreshActionBarVisibility(); // 選中牌時隱藏 action bar；取消選取後若仍輪到玩家，會再次顯示
      },
      onConfirm: (tile) => this.onPlayerConfirmDiscard(tile),
      // 預覽模式維持單擊（視覺驗收與截圖腳本依賴）；正式對局依設定
      isDoubleTap: () => !this.preview && !this.stepMode && loadSettings().doubleTapDiscard,
    });

    // Phaser 偶爾會在 action bar 銷毀／重建後漏掉整次 POINTER_DOWN，連 scene 級事件也收不到。
    // 因此直接監聽 Canvas 原生 pointerdown；capture 階段先命中目前畫面上的牌，
    // 後續 Phaser 若也收到同一次事件，selection 已存在便會由既有去重條件略過。
    const nativeHandPointerFallback = new CNativeHandPointerFallback<TTile>({
      canSelect: () => this.canPlayerSelectTile(),
      hasPendingSelection: () => this.selection.getSelectedTile() !== null,
      hitTest: (worldX, worldY) =>
        this.handViews[this.playerSeat].getConcealedTileAtPoint(worldX, worldY),
      onActivate: (tile, worldX, worldY) => {
        this.traceRound('ui.hand.click.native-fallback', {
          tile,
          pointer: { x: worldX, y: worldY },
        });
        this.selection.handleTileClick(tile);
      },
    });
    const canvas = this.game.canvas;
    const handleNativeHandPointer = (event: PointerEvent): void => {
      if (event.button !== 0) return;
      const bounds = canvas.getBoundingClientRect();
      nativeHandPointerFallback.handle({
        clientX: event.clientX,
        clientY: event.clientY,
        canvasLeft: bounds.left,
        canvasTop: bounds.top,
        canvasWidth: bounds.width,
        canvasHeight: bounds.height,
        worldWidth: LOGICAL_WIDTH,
        worldHeight: LOGICAL_HEIGHT,
      });
    };
    canvas.addEventListener('pointerdown', handleNativeHandPointer, {
      capture: true,
    });
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      canvas.removeEventListener('pointerdown', handleNativeHandPointer, {
        capture: true,
      });
    });

    // Phaser 在 action button 的 pointerup 內同步重建本家手牌時，少數情況不會把下一次
    // game-object pointerdown 送到新 sprite。scene 級 POINTER_DOWN 仍會觸發，因此用實際座標補送。
    // 去重只看「同一次點擊是否已產生升起牌」，不保留跨 pointerdown 的旗標，避免吞掉吃／碰後第一下。
    this.input.on(Phaser.Input.Events.POINTER_DOWN, (pointer: Phaser.Input.Pointer) => {
      if (
        !shouldHandleHandTilePointerFallback({
          canSelect: this.canPlayerSelectTile(),
          hasPendingSelection: this.selection.getSelectedTile() !== null,
        })
      )
        return;
      const tile = this.handViews[this.playerSeat].getConcealedTileAtPoint(
        pointer.worldX,
        pointer.worldY
      );
      if (!tile) return;
      this.traceRound('ui.hand.click.fallback', {
        tile,
        pointer: { x: pointer.worldX, y: pointer.worldY },
      });
      this.selection.handleTileClick(tile);
    });

    this.seatBadges = new CSeatBadges(this);
    this.actionIcon = new CActionIcon(this); // 須在首次 renderSeatBadges 前建立（聽持續標由它管）
    this.renderSeatBadges();
    this.turnHand = new CTurnIndicatorHand(this);
    this.flowerBanner = new CFlowerBanner(this);
    this.tenpaiPeek = new CTenpaiPeek(this);
    this.tenpaiBanner = new CTenpaiBanner(this);
    this.winCeremony = new CWinCeremony(this);
    this.gameDrawCeremony = new CGameDrawCeremony(this);

    this.discardPool = new CDiscardPool(this);
    this.actionBar = new CActionBar(this);
    this.discardAnnounce = new CDiscardAnnounce(this);
    this.playerStatus = new CPlayerStatus(this);

    const initialSettings = loadSettings();
    this.pauseDialog = new CPauseDialog(this, {
      settings: initialSettings,
      onContinue: () => this.resumeGame(),
      onBackHome: () => this.leaveRoundForHome(),
      onSettingsChanged: (patch) => {
        const settings = updateSettings(patch);
        this.audio.refreshSettings(settings);
        this.turnTimer.setDurationSec(settings.discardTimeout);
        this.timerSettingsChangedWhilePaused = true;
        return settings;
      },
    });

    // 倒數計時：CTurnTimer 自帶顯示（位置常數在 src/ui/turn-timer.ts）。
    // - 真實模式逾時 → 啟用直到當局結束的整局代打
    // - 預覽模式逾時 → 自動重新開始（demo 用，畫面永遠在跑）
    this.turnTimer = new CTurnTimer(this, {
      durationSec: initialSettings.discardTimeout,
      onTimeout: () => {
        this.traceRound('ui.turn-timer.timeout');
        if (this.preview) {
          this.turnTimer.start();
          return;
        }
        this.ai.enablePermanentProxy();
      },
    });
  }

  /**
   * 牌桌中央資訊面板：MOAI CITY logo + 「圈風 + 局風 + 底 + 台」
   * 由 CTableInfo 元件處理所有渲染細節；本 scene 只負責決定座標與當前局資料。
   */
  private createCenterDecorations(): void {
    const prevailingWind = this.round.getState().config.prevailingWind;
    const roundIndex: TRoundIndex = (this.roundPlan?.roundIndex ?? 1) as TRoundIndex;
    this.tableInfo = new CTableInfo(this, {
      centerX: LOGICAL_WIDTH / 2,
      centerY: LOGICAL_HEIGHT / 2 + TABLE_INFO_Y_OFFSET_FROM_MID,
      prevailingWind,
      roundIndex,
      basePoints: this.roundPlan?.base ?? TABLE_INFO_BASE_POINTS,
      fanPoints: this.roundPlan?.taiUnit ?? TABLE_INFO_FAN_POINTS,
    });
  }

  /** 顯示回合提示：手勢維持透明，改由該家門前風字 active 樣式提示。 */
  private showTurnIndicator(seat: TSeat, options?: { readonly fadeIn?: boolean }): void {
    this.activeTurnSeat = seat;
    this.renderSeatBadges();
    this.turnHand.show(seat, this.playerSeat, options);
  }

  /** 動作、補花、捨牌落定前取消所有風字高亮，並移除透明手勢物件。 */
  private hideTurnIndicator(): void {
    this.turnHand.hide();
    if (this.activeTurnSeat === null) return;
    this.activeTurnSeat = null;
    this.renderSeatBadges();
  }

  /** 玩家選定捨牌時立即取消風字高亮；透明手勢仍沿用原本淡出生命週期。 */
  private fadeOutTurnIndicator(): void {
    this.turnHand.fadeOut();
    if (this.activeTurnSeat === null) return;
    this.activeTurnSeat = null;
    this.renderSeatBadges();
  }

  /** 把當前狀態翻成 seatWinds + activeSeat，交給 CSeatBadges 渲染。 */
  private renderSeatBadges(): void {
    const seatWinds = this.preview ? this.preview.computeSeatWinds() : this.realSeatWinds();
    const dealerSeat: TSeat =
      this.preview?.getDealerSeat() ?? this.round.getState().config.dealerSeat;
    this.seatBadges.render({
      seatWinds,
      dealerSeat,
      activeSeat: this.activeTurnSeat,
      playerSeat: this.playerSeat,
    });
    // 聽牌「聽」持續標：真實模式讀 round 宣告聽集合；預覽模式讀 demo 集合（顯示在各家手牌區）
    const tenpaiSeats = this.preview
      ? this.previewTenpaiSeats
      : this.round.getState().declaredTenpai;
    this.actionIcon.setTenpaiSeats(tenpaiSeats);
  }

  /** 真實模式 4 家風位：直接讀 round.config.seatWinds。 */
  private realSeatWinds(): Record<TSeat, TWind> {
    const sw = this.round.getState().config.seatWinds;
    return { 0: sw[0], 1: sw[1], 2: sw[2], 3: sw[3] };
  }

  // ----------------------------------------------------------------------
  // 事件訂閱
  // ----------------------------------------------------------------------

  /**
   * 把核心事件與當下 scene 狀態輸出成可直接複製的單行 JSON。
   * Chrome DevTools Console 輸入 RoundTrace 即可只看這些紀錄。
   */
  private traceRound(event: string, detail: Record<string, unknown> = {}): void {
    const state = this.round.getState();
    this.roundTraceSequence += 1;
    const entry = {
      sequence: this.roundTraceSequence,
      timestamp: new Date().toISOString(),
      event,
      core: {
        phase: this.round.getPhase(),
        currentSeat: state.currentSeat,
        playerSeat: this.playerSeat,
        wallRemaining: liveWallRemaining(state.wall),
        liveWallRemaining: liveWallRemaining(state.wall),
        deadWallCount: state.wall.deadWallCount,
        completedKongCount: state.wall.completedKongCount,
        pendingDiscard: state.pendingDiscard,
        pendingAddedKong: state.pendingAddedKong,
        pendingReactionSeats: [...state.pendingReactionSeats],
        pendingReactions: [...state.pendingReactions.entries()].map(([seat, legal]) => ({
          seat,
          canHu: legal.canHu,
          reactions: legal.reactions.map((reaction) => reaction.type),
        })),
        receivedReactions: [...state.receivedReactions.entries()].map(([seat, action]) => ({
          seat,
          action: action.type,
        })),
        concealedCounts: state.hands.map((hand) => hand.concealed.length),
        meldCounts: state.hands.map((hand) => hand.melds.length),
        discardCounts: state.discards.map((discards) => discards.length),
      },
      session: this.activeSession
        ? {
            sessionId: this.activeSession.sessionId,
            roundId: this.roundPlan?.roundId ?? null,
            transactionId: this.roundPlan?.transactionId ?? null,
            sessionStatus: this.activeSession.status,
            prevailingWind: this.roundPlan?.prevailingWind ?? null,
            roundIndex: this.roundPlan?.roundIndex ?? null,
            dealerSeat: this.roundPlan?.dealerSeat ?? null,
            dealerStreak: this.roundPlan?.dealerStreak ?? null,
            fakeEastSeat: this.activeSession.fakeEastSeat,
            openingSeat: this.roundPlan?.openingSeat ?? null,
            seatWinds: this.roundPlan?.seatWinds ?? null,
            dealerDice: this.roundPlan?.dealerDice ?? null,
            openingDice: this.roundPlan?.openingDice ?? null,
            wallSeed: this.roundPlan?.wallSeed ?? null,
            base: this.roundPlan?.base ?? null,
            taiUnit: this.roundPlan?.taiUnit ?? null,
            balances: loadGameSave().ledger.balances,
            settlementApplied: this.roundPlan?.settlementApplied ?? null,
            nextDealerSeat: this.roundPlan ? null : this.activeSession.dealerSeat,
            nextDealerStreak: this.roundPlan ? null : this.activeSession.dealerStreak,
          }
        : null,
      ui: {
        playerActionPending: this.playerActionPending,
        canPlayerSelectTile: this.canPlayerSelectTile(),
        selectedTileId: this.selection.getSelectedTile()?.id ?? null,
        actionBarVisible: this.actionBar.isVisible(),
        actionBarExpanded: this.actionBar.isExpanded(),
        roundEnded: this.roundEnded,
        stepMode: this.stepMode,
        animatingPlayerDiscard: this.animatingPlayerDiscard,
        animatingPlayerDraw: this.animatingPlayerDraw,
        animatingTenpaiPeek: this.animatingTenpaiPeek,
        animatingFlower: this.animatingFlower,
        tenpaiOfferActive: this.tenpaiOfferActive,
        gatedAISeats: [...this.gatedAISeats],
        permanentProxy: this.ai.isPermanentProxy(),
        turnHandSuppressedUntilDiscardSeat: this.turnHandSuppressedUntilDiscardSeat,
        waitingForDiscardLanding: this.discardLandingGate.isWaiting(),
        waitingForMeldIcon: this.meldTurnGate.isWaiting(),
        waitingForFlowerIcon: this.flowerTurnGate.isWaiting(),
      },
      recent: {
        lastDiscarded: this.lastDiscarded,
        lastDrawnTileIds: this.lastDrawnPerSeat.map((tile) => tile?.id ?? null),
        pendingDrawnTileIds: this.pendingDrawnPerSeat.map((tile) => tile?.id ?? null),
      },
      detail,
    };
    console.info(`${ROUND_TRACE_PREFIX} ${JSON.stringify(entry)}`);
  }

  /** 追蹤所有會改變一局進度的公開事件；先於 UI listeners 註冊，保留事件抵達瞬間的狀態。 */
  private subscribeRoundTrace(): void {
    this.bus.on('round.start', (p) => this.traceRound('event.round.start', p));
    this.bus.on('wall.dealt', (p) =>
      this.traceRound('event.wall.dealt', {
        remaining: p.remaining,
        concealedCounts: p.hands.map((hand) => hand.concealed.length),
      })
    );
    this.bus.on('tile.drawn', (p) => this.traceRound('event.tile.drawn', p));
    this.bus.on('tile.discarded', (p) => this.traceRound('event.tile.discarded', p));
    this.bus.on('discard.pending', (p) => this.traceRound('event.discard.pending', p));
    this.bus.on('discard.committed', (p) => this.traceRound('event.discard.committed', p));
    this.bus.on('kong.added-declared', (p) => this.traceRound('event.kong.added-declared', p));
    this.bus.on('tile.flower-replaced', (p) => this.traceRound('event.tile.flower-replaced', p));
    this.bus.on('player.action', (p) => this.traceRound('event.player.action', p));
    this.bus.on('meld.formed', (p) => this.traceRound('event.meld.formed', p));
    this.bus.on('action.available', (p) => this.traceRound('event.action.available', p));
    this.bus.on('tenpai.declared', (p) => this.traceRound('event.tenpai.declared', p));
    this.bus.on('round.ended', (p) => this.traceRound('event.round.ended', p));
  }

  private subscribeEvents(): void {
    this.bus.on('wall.dealt', (p) => {
      // 注意：用 p.hands 而非 refreshHandView，因為 round state 此刻已是發完牌的版本，等價
      for (let s = 0; s < 4; s++) {
        this.refreshHandView(s as TSeat);
      }
      this.discardSequence = []; // 新局：海底清空
      this.discardLandingGate.cancel();
      this.flowerTurnGate.cancel();
      this.meldTurnGate.cancel();
      this.pendingActionAfterIcons = null;
      this.turnHandSuppressedUntilDiscardSeat = null;
      this.pendingAddedKong = null;
      this.discardAnnounce.clear(); // 新局：清掉門前預告
      this.discardPool.update(this.discardSequence);
      this.wallCounter.setRemainingWall(p.remaining);
    });

    this.bus.on('tile.drawn', (p) => {
      if (this.discardLandingGate.holdDraw(p)) {
        this.traceRound('ui.tile-draw.deferred-until-discard-landed', {
          seat: p.seat,
          tile: p.tile,
        });
        return;
      }
      this.processTileDrawn(p);
    });

    this.bus.on('tile.discarded', (p) => {
      this.discardLandingGate.begin();
      this.hideTurnIndicator(); // 該家已出牌，回合結束 → 取消風字高亮
      this.turnHandSuppressedUntilDiscardSeat = null;
      this.lastDiscarded = { tile: p.tile, fromSeat: p.seat };
      this.pendingDrawnPerSeat[p.seat] = null; // 該家已決定出牌 → 解除「分離摸牌」狀態
      this.refreshHandView(p.seat);
      const isPlayer = p.seat === this.playerSeat;
      this.selection.clear();
      // selection.clear 已會重畫玩家手牌；丟牌方不是玩家時也要更新（讓邊框消失）
      if (!isPlayer) this.updatePlayerHandView();
      this.stopTimer();
    });

    // 有人可對這張捨牌反應：不分出牌者座位，牌都先停在其門前，不進海底。
    this.bus.on('discard.pending', (p) => {
      this.discardAnnounce.showPending(p.seat, p.tile);
    });

    // 所有人都放棄（或原本就無人可反應）後，這張牌才正式進入海底。
    this.bus.on('discard.committed', (p) => {
      const renderPool = (): void => {
        this.discardSequence.push(p.tile);
        this.discardPool.update(this.discardSequence, {
          fadeInLast: true,
          onFadeInComplete: () => this.releaseTurnAfterDiscardLanding(),
        });
        this.audio.playDiscardLanding();
      };
      // 等待反應中的門前牌直接與海底淡入接力。
      if (this.discardAnnounce.commitToPool(p.seat, renderPool)) return;

      const isPlayer = p.seat === this.playerSeat;
      // 無人可反應的普通出牌維持既有節奏：別家先短暫門前預告，本家直接進海底。
      if (!isPlayer && this.discardAnnounceEnabled) {
        this.discardAnnounce.show(p.seat, p.tile, renderPool);
      } else {
        renderPool();
      }
    });

    // 加槓先把第四張推出、停在宣告者門前；此時只開放其他家搶槓胡／過。
    // 沒人能胡時核心會緊接著 emit meld.formed，把牌正式疊到既有碰牌上。
    this.bus.on('kong.added-declared', (p) => {
      this.hideTurnIndicator();
      this.pendingAddedKong = { tile: p.tile, fromSeat: p.seat };
      this.lastDiscarded = { tile: p.tile, fromSeat: p.seat };
      this.refreshHandView(p.seat);
      this.discardAnnounce.showPending(p.seat, p.tile);
    });

    this.bus.on('meld.formed', (p) => {
      // 被吃／碰／槓的捨牌不進海底，取消等待；後續 action.available 直接走副露流程。
      this.discardLandingGate.cancel();
      this.meldTurnGate.begin(p.seat);
      // 吃／碰／槓成立後會立刻進入同一家的出牌決策；此段只顯示動作字樣，
      // 不再同時出現指向該家的回合手勢，直到該家打出牌才解除抑制。
      this.hideTurnIndicator();
      this.turnHandSuppressedUntilDiscardSeat = p.seat;
      this.lastDrawnPerSeat[p.seat] = null;
      this.pendingDrawnPerSeat[p.seat] = null;
      // 原版先播吃／碰／槓彈入字樣，完成後副露與手牌才落定並放行下一個出牌決策。
      this.actionIcon.show(p.seat, p.meld.type, () => this.releaseTurnAfterMeldIcon(p.seat));
      // 吃／碰／明槓取用的是仍停在出牌者門前的牌：直接淡出並進入副露，不落海底。
      if (p.meld.type === MeldType.KongAdded) {
        this.pendingAddedKong = null;
        this.discardAnnounce.dismiss(p.seat);
      } else if (p.meld.fromSeat !== undefined) {
        this.discardAnnounce.dismiss(p.meld.fromSeat);
      }
    });

    // 對局中摸到花（drawWithFlowerReplacement）：在該家風字位置播「花」字（放大淡出）。
    // 開局補花階段（RoundPhase.Flower）有自己的橫幅動畫，這裡只處理對局途中（Turn）的摸花。
    this.bus.on('tile.flower-replaced', (p) => {
      // 花牌尚在補牌流程，不是可出牌決策點；不論開局或對局途中都先收掉回合手勢。
      // 等補到非花牌後，action.available 會在真正輪到該家出牌時重新顯示。
      this.hideTurnIndicator();
      if (
        this.round.getPhase() !== RoundPhase.Turn &&
        this.round.getPhase() !== RoundPhase.InitialKong
      ) {
        return;
      }
      this.flowerTurnGate.begin(p.seat);
      this.traceRound('ui.flower-icon.started', { seat: p.seat });
      this.actionIcon.showFlower(p.seat, () => this.releaseTurnAfterFlowerIcon(p.seat));
    });

    // 任一家宣告聽牌（玩家 onDeclareTenpai 或 AI declare-tenpai）：播聽牌橫幅飛向該家座位，
    // 橫幅落定後在各家手牌區顯示「聽」持續標（比照原版：先橫幅、後聽標）。
    this.bus.on('tenpai.declared', (p) => {
      if (this.preview) return; // 預覽 demo 由 previewTenpaiCeremony 自行播放
      this.tenpaiBanner.play(SEAT_BADGE_SPOTS[p.seat], () =>
        this.actionIcon.setTenpaiSeats(this.round.getState().declaredTenpai)
      );
    });

    // 出牌後聽牌偵測：本家出牌使手牌成聽 → 暫停 AI、待玩家決定是否宣告聽
    // （tenpai.changed 在 applyDiscard 內、action.available 之前 emit，搶先把 tenpaiOfferActive 立起來，
    //   後續其他家 action.available 才會被 gating 攔住）
    this.bus.on('tenpai.changed', (p) => {
      if (this.preview || this.stepMode || this.roundEnded) return;
      if (p.seat !== this.playerSeat) return;
      if (this.ai.isProxyActive()) return;
      if (this.round.hasDeclaredTenpai(this.playerSeat)) return; // 已聽 → 自動模式，不再詢問
      if (p.listeningTiles.length === 0) return; // 出牌後沒聽 → 不詢問
      this.tenpaiOfferActive = true; // 攔住後續 AI 排程；實際 action bar 由 onPlayerConfirmDiscard 後顯示
    });

    this.bus.on('action.available', (p) => {
      if (this.roundEnded) return;
      // 核心已進下一個 Turn，但捨牌仍在門前／海底淡入中：先暫存整個回合 UI 與 AI，
      // 動畫完成後由 releaseTurnAfterDiscardLanding 統一放行。
      if (this.round.getPhase() === RoundPhase.Turn && this.discardLandingGate.hold(p)) {
        this.traceRound('ui.turn-action.deferred-until-discard-landed', {
          seat: p.seat,
        });
        return;
      }
      this.processActionAvailable(p);
    });

    this.bus.on('round.ended', (p) => {
      const roundState = this.round.getState();
      const resultSummary = addDealerRelationToResultSummary(
        p.summary,
        roundState.config.dealerSeat,
        this.roundPlan?.dealerStreak ?? roundState.config.dealerStreak ?? 0
      );
      const names = [0, 1, 2, 3].map(
        (seat) => this.activeSession?.players.find((player) => player.seat === seat)?.name
      );
      const chips = this.settleRoundForGameSet(p.summary);
      const resultData = buildResultFlowData({
        names,
        summary: resultSummary,
        hands: roundState.hands,
        seatWinds: roundState.config.seatWinds,
        dealerSeat: roundState.config.dealerSeat,
        ...(chips ? { chips } : {}),
      });
      this.discardLandingGate.cancel();
      this.flowerTurnGate.cancel();
      this.meldTurnGate.cancel();
      this.pendingActionAfterIcons = null;
      this.roundEnded = true;
      this.playerActionPending = false;
      this.tenpaiOfferActive = false;
      this.gatedAISeats = [];
      this.animatingTenpaiPeek = false;
      this.tenpaiPeek.destroy();
      this.tenpaiBanner.destroy();
      this.stopTimer();
      this.hideTurnIndicator();
      this.turnHandSuppressedUntilDiscardSeat = null;
      this.pendingAddedKong = null;
      this.actionBar.clear();
      this.actionIcon.clear();
      this.ai.destroy();
      this.playerStatus.hideThinking();
      this.playerStatus.setProxyActive(false);
      this.discardAnnounce.clear();
      this.selection.clear();

      if (p.summary.kind !== RoundSummaryKind.Hu) {
        this.traceRound('ui.game-draw-ceremony.start');
        this.gameDrawCeremony.play(() => {
          this.traceRound('ui.game-draw-ceremony.continue');
          // 原 APP 流局不經過四家攤牌／台數頁，直接進籌碼結算。
          this.scene.start(SceneKey.ResultMoney, resultData);
        });
        return;
      }

      const plan = buildWinCeremonyPlan(p.summary);
      if (!plan) {
        this.scene.start(SceneKey.ResultTile, resultData);
        return;
      }

      this.traceRound('ui.win-ceremony.start', {
        kind: plan.kind,
        winnerSeats: plan.winnerSeats,
        loserSeats: plan.loserSeats,
        discardedTiles: plan.discardedTiles,
      });
      this.winCeremony.play(plan, {
        playWinSound: (isSelfDraw) => this.audio.playWinCeremony(isSelfDraw),
        onRevealWinners: () => {
          this.traceRound('ui.win-ceremony.reveal-winners', {
            winnerSeats: plan.winnerSeats,
          });
          for (const winnerSeat of plan.winnerSeats) {
            // 結算頁資料會把「胡張」補進贏家手牌以便算台；牌桌儀式則必須使用
            // round 原始手牌，放槍牌才不會被錯算成贏家原有的牌。
            const ceremonyHand = roundState.hands[winnerSeat];
            const selfDrawResult =
              p.summary.kind === RoundSummaryKind.Hu
                ? p.summary.results.find(
                    (result) => result.seat === winnerSeat && result.isSelfDraw
                  )
                : undefined;
            if (selfDrawResult) {
              this.handViews[winnerSeat].update(ceremonyHand, undefined, {
                drawnTileId: selfDrawResult.winTile.id,
                selfDrawReveal: true,
              });
            } else {
              this.handViews[winnerSeat].update(ceremonyHand, undefined, {
                revealConcealed: true,
              });
            }
          }
        },
        onContinue: () => {
          this.traceRound('ui.win-ceremony.continue');
          this.scene.start(SceneKey.ResultTile, resultData);
        },
      });
    });
  }

  private settleRoundForGameSet(summary: TRoundSummary): ReadonlyArray<TResultChip> | null {
    if (isRemoteRound(this.round)) return this.applyServerSettlement();
    const session = this.activeSession;
    const plan = this.roundPlan;
    if (!session || !plan) return null;

    const saveBefore = loadGameSave();
    const balancesBefore = saveBefore.ledger.balances;
    const result = summary.kind === RoundSummaryKind.Hu ? summary.results[0] : undefined;
    const winnerSeat = result?.seat ?? null;
    const deltaBySeat: readonly [number, number, number, number] = result
      ? computeSettlement({
          winnerSeat: result.seat,
          loserSeat: result.loserSeat,
          isSelfDraw: result.isSelfDraw,
          handTai: result.scoreItems
            .filter((item) => item.key !== 'dealer' && item.key !== 'lianzhuang')
            .reduce((total, item) => total + item.points, 0),
          dealerSeat: plan.dealerSeat,
          dealerStreak: plan.dealerStreak,
          base: plan.base,
          taiUnit: plan.taiUnit,
          balancesBefore,
        }).deltaBySeat
      : [0, 0, 0, 0];
    const balancesAfter = [
      balancesBefore[0] + deltaBySeat[0],
      balancesBefore[1] + deltaBySeat[1],
      balancesBefore[2] + deltaBySeat[2],
      balancesBefore[3] + deltaBySeat[3],
    ] as const;
    traceGameSet(
      'round.settlement.computed',
      session,
      { deltaBySeat, balancesAfter },
      balancesBefore
    );
    const transitionedSession = advanceGameSetAfterRound(session, winnerSeat);
    const nextSession = canAllPlayersAfford(balancesAfter, session.bet)
      ? transitionedSession
      : completeGameSetForInsufficientBalance(transitionedSession);
    const persisted = applySettlementTransaction({
      transactionId: plan.transactionId,
      deltaBySeat,
      activeSession: nextSession,
    });
    traceGameSet(
      'round.settlement.applied',
      nextSession,
      {
        transactionId: plan.transactionId,
        applied: persisted.applied,
        deltaBySeat,
      },
      persisted.save.ledger.balances
    );
    traceGameSet(
      'round.transition.computed',
      nextSession,
      {
        nextDealerSeat: nextSession.dealerSeat,
        nextDealerStreak: nextSession.dealerStreak,
      },
      persisted.save.ledger.balances
    );
    if (nextSession.status === GameSetStatus.CircleComplete) {
      traceGameSet('circle.completed', nextSession, {}, persisted.save.ledger.balances);
    }
    if (nextSession.status === GameSetStatus.Completed) {
      traceGameSet(
        nextSession.completionReason === GameSetCompletionReason.InsufficientBalance
          ? 'session.blocked.insufficient-balance'
          : 'session.completed',
        nextSession,
        {},
        persisted.save.ledger.balances
      );
    }
    this.traceRound('round.settlement.applied', {
      transactionId: plan.transactionId,
      applied: persisted.applied,
      deltaBySeat,
      nextSessionStatus: nextSession.status,
      nextDealerSeat: nextSession.dealerSeat,
      nextDealerStreak: nextSession.dealerStreak,
    });
    this.activeSession = persisted.save.activeSession;
    this.roundPlan = null;

    const effectiveBefore = persisted.applied
      ? balancesBefore
      : ([
          persisted.save.ledger.balances[0] - deltaBySeat[0],
          persisted.save.ledger.balances[1] - deltaBySeat[1],
          persisted.save.ledger.balances[2] - deltaBySeat[2],
          persisted.save.ledger.balances[3] - deltaBySeat[3],
        ] as const);
    return buildResultChips(effectiveBefore, deltaBySeat);
  }

  /** 海底最後一張淡入完成後，才放行下一回合 action（含手勢、玩家 UI、AI 排程）。 */
  private releaseTurnAfterDiscardLanding(): void {
    const pendingDraw = this.discardLandingGate.takeDraw();
    const pending = this.discardLandingGate.complete();
    this.traceRound('ui.discard-landing.complete', {
      releasedDrawSeat: pendingDraw?.seat ?? null,
      releasedSeat: pending?.seat ?? null,
    });
    if (this.roundEnded) return;
    if (pendingDraw) this.processTileDrawn(pendingDraw);
    if (pending) this.processActionAvailable(pending);
  }

  /** 套用 tile.drawn 的 scene 視覺；若上一張仍在落海底，會由 landing callback 延後呼叫。 */
  private processTileDrawn(p: TRoundEventMap['tile.drawn']): void {
    this.lastDrawnPerSeat[p.seat] = p.tile;
    this.pendingDrawnPerSeat[p.seat] = p.tile; // 摸到、未決定 → 分離顯示在手牌成長端（四家一致）
    this.wallCounter.setRemainingWall(p.remaining);
    // 補花動畫期間：手牌重建交給 animateFlowerReplacement，這裡不重建（只更新剩牌數）
    if (this.animatingFlower) return;
    // 摸進的牌一律「分離顯示在手牌右方」，並在首次出現時從透明淡入。
    this.refreshHandView(p.seat, { fadeInDrawnTile: true });
  }

  /** 實際處理 action.available；可能由事件當下立即執行，或海底動畫完成後延後執行。 */
  private processActionAvailable(p: TRoundEventMap['action.available']): void {
    if (this.meldTurnGate.isWaiting() || this.flowerTurnGate.isWaiting()) {
      this.pendingActionAfterIcons = p;
      this.traceRound('ui.action.deferred-until-icons-complete', {
        seat: p.seat,
      });
      return;
    }
    if (this.meldTurnGate.hold(p.seat, p)) {
      this.traceRound('ui.turn-action.deferred-until-meld-icon-complete', {
        seat: p.seat,
      });
      return;
    }
    if (this.flowerTurnGate.hold(p.seat, p)) {
      this.traceRound('ui.turn-action.deferred-until-flower-icon-complete', {
        seat: p.seat,
      });
      return;
    }
    // 聽牌詢問中（玩家出牌後跳出「聽 / 放棄」）：暫停其他家 AI，待玩家決定後 flushGatedAI 放行
    if (this.tenpaiOfferActive && p.seat !== this.playerSeat) {
      this.gatedAISeats.push(p.seat);
      return;
    }
    // 回合指示手勢：Turn phase 該家要出牌 → 在該家風字旁顯示手勢（真實 / 逐步模式都要）
    // 例外：本家摸牌整理動畫進行中，手勢延到動畫結束才淡入（見 animatePlayerDraw onComplete），
    //       以及本家已宣告聽牌（自動瞇牌摸打、不顯示手勢）。
    if (
      this.round.getPhase() === RoundPhase.Turn &&
      this.turnHandSuppressedUntilDiscardSeat !== p.seat &&
      !(p.seat === this.playerSeat && this.animatingPlayerDraw) &&
      !(p.seat === this.playerSeat && this.round.hasDeclaredTenpai(this.playerSeat))
    ) {
      this.showTurnIndicator(p.seat);
    }
    // 逐步模式：不自動排程任何動作，停在當前決策點等玩家按 N（由 stepOnce 推進）
    if (this.stepMode) return;
    if (p.seat === this.playerSeat) {
      // tile.drawn / tile.discarded / meld.formed 已先完成手牌重繪；
      // 此處不重建，避免中斷剛摸進牌的淡入動畫。
      this.handlePlayerActionAvailable(p);
      this.traceRound('ui.player-action.ready', {
        canHu: p.canHu,
        reactions: p.reactions,
      });
    } else {
      // 非自家行動：確保倒數計時不殘留在畫面上
      this.stopTimer();
      this.configureActionBarPersistentControls();
      this.refreshActionBarVisibility();
      this.traceRound('ai.action.scheduled', { seat: p.seat });
      this.ai.schedule(p.seat);
    }
  }

  /** 吃碰槓字樣淡出後，副露／手牌才落定，接著放行同家的出牌決策。 */
  private releaseTurnAfterMeldIcon(seat: TSeat): void {
    const pending = this.meldTurnGate.complete(seat);
    this.refreshHandView(seat);
    this.traceRound('ui.meld-icon.complete', {
      seat,
      releasedSeat: pending?.seat ?? null,
    });
    if (this.roundEnded) return;
    if (this.releaseActionAfterIcons()) return;
    if (!pending) return;
    if (
      this.round.getPhase() !== RoundPhase.Turn ||
      this.round.getState().currentSeat !== pending.seat
    ) {
      this.traceRound('ui.meld-icon.stale-action-dropped', {
        seat,
        releasedSeat: pending.seat,
      });
      return;
    }
    this.processActionAvailable(pending);
  }

  /** 補花圖示 fade out 完成後，才放行該家的回合手勢、玩家 UI 或 AI 排程。 */
  private releaseTurnAfterFlowerIcon(seat: TSeat): void {
    const pending = this.flowerTurnGate.complete(seat);
    this.traceRound('ui.flower-icon.complete', {
      seat,
      releasedSeat: pending?.seat ?? null,
    });
    if (this.roundEnded) return;
    if (this.releaseActionAfterIcons()) return;
    if (!pending) return;
    if (
      this.round.getPhase() !== RoundPhase.Turn ||
      this.round.getState().currentSeat !== pending.seat
    ) {
      this.traceRound('ui.flower-icon.stale-action-dropped', {
        seat,
        releasedSeat: pending.seat,
      });
      return;
    }
    // 補花期間 tile.drawn 已重建過手牌，但當時 gate 尚未放行，canPlayerSelectTile() 為 false，
    // 因此那些 sprite 沒有 hover 黃框事件。gate 完成後重建一次，恢復本家可選牌的互動狀態。
    if (seat === this.playerSeat) this.updatePlayerHandView();
    this.processActionAvailable(pending);
  }

  /** 槓／花圖示全數結束後，放行可能已切到下一家的決策。 */
  private releaseActionAfterIcons(): boolean {
    if (this.meldTurnGate.isWaiting() || this.flowerTurnGate.isWaiting()) return true;
    const pending = this.pendingActionAfterIcons;
    if (!pending) return false;
    this.pendingActionAfterIcons = null;
    if (this.round.getState().currentSeat !== pending.seat) {
      this.traceRound('ui.action-after-icons.stale-action-dropped', {
        seat: pending.seat,
      });
      return true;
    }
    this.processActionAvailable(pending);
    return true;
  }

  // ----------------------------------------------------------------------
  // 玩家動作
  // ----------------------------------------------------------------------

  private handlePlayerActionAvailable(p: TRoundEventMap['action.available']): void {
    // 整局代打中 → AI 接手出招，不顯示玩家動作選項、不啟動倒數
    if (this.ai.isPermanentProxy()) {
      this.ai.schedule(this.playerSeat);
      return;
    }

    // 聽牌模式：玩家手牌鎖定、由系統自動處理
    //   - 自己 turn：摸牌 → 瞇牌動畫 → 自摸胡 or 把摸的牌打出去
    //   - 別家捨牌反應：聽牌後只胡（榮和）、不再吃碰槓 → 能胡就胡、否則放棄
    if (this.round.hasDeclaredTenpai(this.playerSeat)) {
      if (this.round.getPhase() === RoundPhase.Turn) {
        this.runTenpaiAutoTurn(p);
      } else {
        this.submitPlayerAction(
          p.canHu ? { type: PlayerActionType.Hu } : { type: PlayerActionType.Pass }
        );
      }
      return;
    }

    this.playerActionPending = true;
    if (this.round.getPhase() === RoundPhase.InitialKong) {
      this.showPlayerInitialKongButtons();
      this.startTimer();
    } else if (this.round.getPhase() === RoundPhase.Turn) {
      this.showPlayerTurnButtons(p.canHu);
      this.startTimer();
    } else if (this.round.getPhase() === RoundPhase.WaitingReaction) {
      this.showPlayerReactionButtons(p);
      this.startTimer();
    }
    this.refreshActionBarVisibility();
  }

  /** 起手暗槓階段：每組暗槓各一顆候選按鈕，玩家也可選擇放棄並換下一家。 */
  private showPlayerInitialKongButtons(): void {
    const hand = this.round.getState().hands[this.playerSeat];
    const choices: TActionChoice[] = findKongConcealedOptions(hand).map((option) => ({
      key: ActionKey.Kong,
      handler: () =>
        this.submitPlayerAction({
          type: PlayerActionType.KongConcealed,
          option,
        }),
      tiles: option.handTiles,
    }));
    choices.push({
      key: ActionKey.Pass,
      handler: () => this.submitPlayerAction({ type: PlayerActionType.Pass }),
    });

    this.actionBar.clear();
    this.actionBar.setChoices(choices);
    this.configureActionBarPersistentControls();
    this.actionBar.setVisible(true);
    this.actionBar.expand();
  }

  /**
   * 依當前狀態決定 action bar 是否顯示：
   *   - 預覽模式 → 不干預（由 CGamePreviewController.populate 直接控制）
   *   - 已結束 / 暫停 / 玩家正在預覽選牌 → 隱藏
   *   - 其餘時間保留離開入口；未代打時也保留手動代打入口
   */
  private refreshActionBarVisibility(): void {
    if (this.preview) return;
    const show = this.selection.getSelectedTile() === null && !this.roundEnded && !this.gamePaused;
    this.configureActionBarPersistentControls();
    this.actionBar.setVisible(show);
  }

  // ----------------------------------------------------------------------
  // 聽牌模式
  // ----------------------------------------------------------------------

  /**
   * 出牌後若偵測到聽牌（tenpaiOfferActive 已被 tenpai.changed 立起）：
   * 顯示「聽 / 放棄」action bar 讓玩家選擇是否進入聽牌模式。
   * 由 onPlayerConfirmDiscard 在送出出牌動作後呼叫（兩條出牌路徑都會經過）。
   */
  private maybeShowTenpaiOffer(): void {
    if (!this.tenpaiOfferActive) return;
    if (this.roundEnded) {
      // 出牌被別家榮和等 → 局已結束，放掉 gate
      this.tenpaiOfferActive = false;
      this.flushGatedAI();
      return;
    }
    this.showTenpaiOfferBar();
  }

  /** 顯示只有「聽 / 放棄」的 action bar（併入既有動作條）。 */
  private showTenpaiOfferBar(): void {
    this.playerActionPending = false; // 這不是一般 turn / reaction，不啟動倒數
    this.stopTimer();
    this.actionBar.clear();
    this.actionBar.setChoices([
      { key: ActionKey.Listen, handler: () => this.onDeclareTenpai() },
      { key: ActionKey.Pass, handler: () => this.onDeclineTenpai() },
    ]);
    this.configureActionBarPersistentControls();
    this.actionBar.setVisible(true);
    this.actionBar.expand();
  }

  /** 玩家點「聽」：宣告聽牌（→ tenpai.declared 訂閱播放橫幅、落定後顯示「聽」持續標），放行被暫停的 AI。 */
  private onDeclareTenpai(): void {
    this.tenpaiOfferActive = false;
    this.actionBar.clear();
    this.round.declareTenpai(this.playerSeat); // 記聽（影響計分）+ emit tenpai.declared
    this.flushGatedAI();
  }

  /** 玩家點「放棄」：不宣告聽牌，維持手動出牌；放行被暫停的 AI。 */
  private onDeclineTenpai(): void {
    this.tenpaiOfferActive = false;
    this.actionBar.clear();
    this.refreshActionBarVisibility();
    this.flushGatedAI();
  }

  /** 放行 tenpaiOfferActive 期間被暫停的 AI 座位（玩家決定後）。 */
  private flushGatedAI(): void {
    const seats = this.gatedAISeats;
    this.gatedAISeats = [];
    if (this.roundEnded) return;
    for (const s of seats) {
      if (s === this.playerSeat) continue; // 玩家座位由 action.available 流程自處理
      this.ai.schedule(s);
    }
  }

  /**
   * 在本家剛摸進的牌上播放瞇牌動畫。
   * 牌位取自手牌（找不到用畫面底部中央 fallback），tileScale 用本家 handScale。
   */
  private playPlayerPeek(
    drawn: TTile,
    onRevealed: () => void,
    opts?: { debugPivot?: boolean }
  ): void {
    const pos = this.handViews[this.playerSeat].getConcealedTilePosition(drawn.id) ?? {
      x: PEEK_FALLBACK_X,
      y: PEEK_FALLBACK_Y,
    };
    const tileScale = SEAT_LAYOUT[this.playerSeat].handScale;
    this.tenpaiPeek.play(
      {
        x: pos.x,
        y: pos.y,
        tile: drawn,
        tileScale,
        ...(opts?.debugPivot ? { debugPivot: true } : {}),
      },
      onRevealed
    );
  }

  /**
   * 聽牌模式的自動回合：在最右側摸進來的牌上播放瞇牌動畫，
   * 掀開後若自摸胡就胡；否則直接送出剛摸的牌。只有其他家有反應時，
   * discard.pending 才會讓牌停在門前，沒人反應則由 discard.committed 直接進海底。
   */
  private runTenpaiAutoTurn(p: TRoundEventMap['action.available']): void {
    const drawn =
      this.lastDrawnPerSeat[this.playerSeat] ?? this.pendingDrawnPerSeat[this.playerSeat];
    if (!drawn) {
      // 理論上聽牌 turn 一定有剛摸的牌；防衛：找不到就放棄這手（不應發生）
      this.submitPlayerAction({ type: PlayerActionType.Pass });
      return;
    }
    this.animatingTenpaiPeek = true;
    this.hideTurnIndicator();
    this.actionBar.setVisible(false);
    this.updatePlayerHandView(); // 確保摸牌「分離顯示」在最右側
    this.playPlayerPeek(drawn, () => {
      this.animatingTenpaiPeek = false;
      if (this.roundEnded) return;
      this.submitPlayerAction(buildTenpaiAutoTurnAction(drawn, p.canHu));
    });
  }

  /**
   * 預覽模式聽牌 demo（Preview 層按 T）：
   * 自家風字顯示「聽」→ 橫幅飛入 → 用 mock 手牌最後一張示範一次瞇牌，跑完還原（onDone）。
   */
  private previewTenpaiCeremony(onDone: () => void): void {
    const mock = this.preview?.getMockHand();
    this.previewTenpaiSeats = new Set<TSeat>([this.playerSeat]);
    this.renderSeatBadges();
    this.tenpaiBanner.play(SEAT_BADGE_SPOTS[this.playerSeat], () => {
      if (!mock || mock.concealed.length === 0) {
        this.previewTenpaiSeats.clear();
        this.renderSeatBadges();
        onDone();
        return;
      }
      const drawn = mock.concealed[mock.concealed.length - 1]!;
      this.handViews[this.playerSeat].update(mock, undefined, {
        drawnTileId: drawn.id,
      });
      this.playPlayerPeek(drawn, () => {
        this.time.delayedCall(400, () => {
          this.previewTenpaiSeats.clear();
          this.renderSeatBadges();
          onDone();
        });
      });
    });
  }

  /**
   * 預覽模式 G：循環播放瞇牌動畫（含旋轉圓心紅點），方便邊播邊在程式裡調
   * tenpai-peek.ts 的 HAND_* / THUMB_PIVOT_* 等座標常數（存檔 HMR 後再按一次 G 即可）。
   * on=false 停止並清掉動畫（手牌還原由 controller restoreMockState 處理）。
   */
  private previewPeekLoop(on: boolean): void {
    this.peekLoopActive = on;
    if (!on) {
      this.tenpaiPeek.destroy();
      return;
    }
    this.runPeekLoopOnce();
  }

  /** 播一次瞇牌（debug 圓心紅點），播完排下一輪（loop 仍開著的話）。 */
  private runPeekLoopOnce(): void {
    if (!this.peekLoopActive) return;
    const mock = this.preview?.getMockHand();
    if (!mock || mock.concealed.length === 0) {
      this.peekLoopActive = false;
      return;
    }
    const drawn = mock.concealed[mock.concealed.length - 1]!;
    this.handViews[this.playerSeat].update(mock, undefined, {
      drawnTileId: drawn.id,
    });
    this.playPlayerPeek(
      drawn,
      () => {
        // 掀開後等動畫自己淡出收場，再隔一小段排下一輪
        this.time.delayedCall(PEEK_LOOP_GAP_MS, () => this.runPeekLoopOnce());
      },
      { debugPivot: true }
    );
  }

  /**
   * Turn phase（玩家自己摸進的回合）：可能的動作 = 胡 / 暗槓 / 代打；其他按鈕 disabled。
   * 顯示策略：
   *   - 沒有可用的 吃/碰/槓/聽/胡/放棄 → 收合（只露 [離開, 代打]）
   *   - 有任一可用 → 展開動畫（吃碰槓聽胡放棄 從離開鈕位置往左滑出）
   */
  private showPlayerTurnButtons(canHu: boolean): void {
    const hand = this.round.getState().hands[this.playerSeat];
    const concealedKongs = findKongConcealedOptions(hand);
    const drawnTile = this.round.getState().lastDrawnTile;
    const addedKong = drawnTile ? findKongAddedOption(hand, drawnTile) : null;

    this.actionBar.clear();

    // 只建有功能的動作鈕：自摸胡 / 暗槓
    const choices: TActionChoice[] = [];
    if (canHu) {
      // 自摸胡：胡的那張 = 剛摸進的牌
      choices.push({
        key: ActionKey.Hu,
        handler: () => this.submitPlayerAction({ type: PlayerActionType.Hu }),
        ...(drawnTile ? { tiles: [drawnTile], highlightTileId: drawnTile.id } : {}),
      });
    }
    for (const kong of concealedKongs) {
      // 暗槓：手上的四張同種牌
      choices.push({
        key: ActionKey.Kong,
        handler: () =>
          this.submitPlayerAction({
            type: PlayerActionType.KongConcealed,
            option: kong,
          }),
        tiles: kong.handTiles,
      });
    }
    if (addedKong) {
      const pong = hand.melds[addedKong.pongMeldIndex];
      choices.push({
        key: ActionKey.Kong,
        handler: () =>
          this.submitPlayerAction({
            type: PlayerActionType.KongAdded,
            option: addedKong,
          }),
        tiles: pong ? [...pong.tiles, addedKong.drawnTile] : [addedKong.drawnTile],
        highlightTileId: addedKong.drawnTile.id,
      });
    }
    this.actionBar.setChoices(choices);
    this.configureActionBarPersistentControls();

    this.actionBar.setVisible(true);
    // 有可用動作鈕才展開；都沒有就維持收合（只露離開 / 代打）
    if (choices.length > 0) {
      this.actionBar.expand();
    }
  }

  /**
   * WaitingReaction phase（別家剛打了一張，玩家可以攔截）：吃 / 碰 / 槓 / 胡 / 過。
   * 吃可能有多組牌組 → 每組各出一顆「吃」按鈕（依麻將規則最多 3 顆）。
   */
  private showPlayerReactionButtons(p: TRoundEventMap['action.available']): void {
    this.actionBar.clear();

    // 只建有功能的動作鈕；榮和胡的那張 = 剛被捨出的牌
    const winTile = this.lastDiscarded?.tile;
    const choices: TActionChoice[] = [];

    if (p.canHu) {
      choices.push({
        key: ActionKey.Hu,
        handler: () => this.submitPlayerAction({ type: PlayerActionType.Hu }),
        ...(winTile ? { tiles: [winTile], highlightTileId: winTile.id } : {}),
      });
    }

    for (const r of p.reactions) {
      switch (r.type) {
        case DiscardReactionType.Pong:
          // 碰：手上兩張 + 被碰的捨牌（標記捨牌不透明、手牌帶透明，與吃一致）
          choices.push({
            key: ActionKey.Pong,
            handler: () =>
              this.submitPlayerAction({
                type: PlayerActionType.Pong,
                option: r.option,
              }),
            tiles: winTile ? [...r.option.handTiles, winTile] : r.option.handTiles,
            ...(winTile ? { highlightTileId: winTile.id } : {}),
          });
          break;
        case DiscardReactionType.KongExposed:
          // 明槓：手上三張 + 被槓的捨牌（標記捨牌不透明、手牌帶透明，與吃一致）
          choices.push({
            key: ActionKey.Kong,
            handler: () =>
              this.submitPlayerAction({
                type: PlayerActionType.KongExposed,
                option: r.option,
              }),
            tiles: winTile ? [...r.option.handTiles, winTile] : r.option.handTiles,
            ...(winTile ? { highlightTileId: winTile.id } : {}),
          });
          break;
        case DiscardReactionType.Chi:
          // 吃：每一組牌組各一顆按鈕；顯示完成後的順子三張、標記被吃（剛被捨出）的那張
          choices.push({
            key: ActionKey.Chi,
            handler: () =>
              this.submitPlayerAction({
                type: PlayerActionType.Chi,
                option: r.option,
              }),
            tiles: r.option.sequence,
            ...(winTile ? { highlightTileId: winTile.id } : {}),
          });
          break;
      }
    }

    // WaitingReaction 一定有「放棄」
    choices.push({
      key: ActionKey.Pass,
      handler: () => this.submitPlayerAction({ type: PlayerActionType.Pass }),
    });

    this.actionBar.setChoices(choices);
    this.configureActionBarPersistentControls();

    this.actionBar.setVisible(true);
    // WaitingReaction 永遠至少有 Pass 可按，一律展開
    this.actionBar.expand();
  }

  /**
   * CPlayerSelectionController.onConfirm 回呼：玩家在放大預覽上點擊確認出牌。
   * 預覽模式 → mock；真實模式 → 送 round.submitAction。
   */
  private onPlayerConfirmDiscard(tile: TTile): void {
    // 「摸到的牌沒打出去」（打的是別張）→ 出掉選的那張，再把摸的牌以插入動畫收進手牌。
    // 只在真實對局走（預覽 / 逐步用 mock，維持原本補位）。
    const drawn = this.pendingDrawnPerSeat[this.playerSeat];
    if (drawn && tile.id !== drawn.id && !this.preview && !this.stepMode) {
      this.animatingPlayerDraw = true; // 期間略過手牌重建 + 不能選牌，交給單段重排動畫
      this.submitPlayerAction({ type: PlayerActionType.Discard, tile }); // round 移除打出的牌（含發 tile.discarded）
      this.maybeShowTenpaiOffer(); // 出牌後若聽牌 → 彈「聽 / 放棄」
      const afterHand = this.round.getState().hands[this.playerSeat];
      this.handViews[this.playerSeat].animateDiscardAndDrawnMerge(
        afterHand,
        tile.id,
        HAND_DISCARD_MERGE_MS,
        () => {
          this.animatingPlayerDraw = false;
          this.updatePlayerHandView();
        }
      );
      return;
    }

    // 直接打出分離在最外側的摸進牌：其餘 16 張從頭到尾都留在原位，
    // 不製造牌列中的缺口，也不播放補位動畫。
    if (
      !shouldCollapsePlayerHandGap({
        discardedTileId: tile.id,
        ...(drawn ? { drawnTileId: drawn.id } : {}),
      })
    ) {
      this.submitPlayerAction({ type: PlayerActionType.Discard, tile });
      this.maybeShowTenpaiOffer();
      return;
    }

    // 沒有分離摸牌（預覽 / 吃碰後出牌等）：手牌「由左至右」滑動補位。
    // 動畫期間 animatingPlayerDiscard=true → updatePlayerHandView 略過重建，
    // 不會打斷滑動；滑完才依最新狀態重建一次（位置一致、不跳動）。
    this.animatingPlayerDiscard = true;
    this.handViews[this.playerSeat].collapseGap(tile.id, HAND_COLLAPSE_MS, () => {
      this.animatingPlayerDiscard = false;
      this.updatePlayerHandView();
    });

    if (this.preview) {
      // 預覽模式：模擬「打牌到海底」— 從 mockPlayerHand 移除、追加到 discardSequence 尾端（fade in）
      this.preview.discardMockTile(tile);
    } else {
      this.submitPlayerAction({ type: PlayerActionType.Discard, tile });
      this.maybeShowTenpaiOffer(); // 出牌後若聽牌 → 彈「聽 / 放棄」
    }
  }

  /** 取得當前玩家手牌（逐步模式 / 真實模式 → round state；靜態預覽 → controller 內 mock）。 */
  private getCurrentPlayerHand(): THand {
    if (this.stepMode) return this.round.getState().hands[this.playerSeat];
    const mock = this.preview?.getMockHand();
    if (mock) return mock;
    return this.round.getState().hands[this.playerSeat];
  }

  /**
   * 玩家當下是否可以從手牌中挑一張打出。
   * - 預覽模式：永遠可選（互動用 mock 狀態驅動）
   * - 真實模式：必須處於 Turn phase、currentSeat 是玩家、未結束，
   *   且不在整局代打中
   */
  private canPlayerSelectTile(): boolean {
    if (this.roundEnded) return false;
    if (this.gamePaused) return false;
    if (this.discardLandingGate.isWaiting()) return false;
    if (this.flowerTurnGate.isWaiting()) return false;
    if (this.meldTurnGate.isWaiting()) return false;
    if (this.animatingPlayerDraw) return false; // 摸牌整理動畫進行中，先別讓玩家選牌
    if (this.animatingTenpaiPeek) return false; // 聽牌瞇牌動畫進行中，自動打、不讓手選
    if (this.stepMode) return false; // 逐步模式四家全 AI，玩家不選牌
    if (this.preview) return true;
    if (this.ai.isProxyActive()) return false;
    if (this.round.hasDeclaredTenpai(this.playerSeat)) return false; // 聽牌後系統自動摸打
    if (this.round.getPhase() !== RoundPhase.Turn) return false;
    if (this.round.getState().currentSeat !== this.playerSeat) return false;
    return true;
  }

  /**
   * 重繪玩家手牌。單擊出牌模式：不畫黃框。
   * 升起動畫期間：那張在原位「隱形佔位」（保留空位）；補位滑動動畫進行中則整個略過重建。
   */
  private updatePlayerHandView(options?: { readonly fadeInDrawnTile?: boolean }): void {
    if (this.animatingPlayerDiscard) return; // 滑動補位動畫進行中 → 不重建，交給動畫
    if (this.animatingPlayerDraw) return; // 摸牌整理動畫進行中 → 不重建，交給 animateDraw
    const hand = this.getCurrentPlayerHand();
    const pending = this.selection.getSelectedTile();
    const drawn = this.pendingDrawnPerSeat[this.playerSeat];
    const addedKongTile =
      this.pendingAddedKong?.fromSeat === this.playerSeat ? this.pendingAddedKong.tile : null;
    // 桌機 + 輪到本家出牌 + 尚未選牌 → 手牌 hover 顯示外框 + pointer 游標
    const hoverHighlight = supportsHover() && pending === null && this.canPlayerSelectTile();
    const keyboardFocusedTile =
      !this.keyboardFocusOnActionBar && this.canPlayerSelectTile() && hand.concealed.length > 0
        ? hand.concealed[Math.min(this.keyboardHandIndex, hand.concealed.length - 1)]
        : undefined;
    this.handViews[this.playerSeat].update(hand, undefined, {
      showBorders: keyboardFocusedTile !== undefined,
      ...(keyboardFocusedTile ? { focusedTileId: keyboardFocusedTile.id } : {}),
      hoverHighlight,
      // 升起中那張在原位留空（隱形）；無升起時不帶此 key
      ...(pending || addedKongTile ? { hiddenTileId: pending?.id ?? addedKongTile!.id } : {}),
      // 摸到、未決定的牌始終分離顯示在手牌右方；若正升起這張，僅隱藏最外側原牌，
      // 其餘 16 張不重新排序，因此不會在牌列中形成缺口或移動。
      ...(drawn ? { drawnTileId: drawn.id } : {}),
      ...(options?.fadeInDrawnTile ? { fadeInDrawnTile: true } : {}),
    });
  }

  /**
   * 重繪某座位的手牌（player → updatePlayerHandView；其他家 → 直接 update）。
   * 統一入口，方便事件 handler 只 call 一個方法。
   */
  private refreshHandView(seat: TSeat, options?: { readonly fadeInDrawnTile?: boolean }): void {
    if (seat === this.playerSeat) {
      this.updatePlayerHandView(options);
    } else {
      const drawn = this.pendingDrawnPerSeat[seat];
      const addedKongTile =
        this.pendingAddedKong?.fromSeat === seat ? this.pendingAddedKong.tile : null;
      this.handViews[seat].update(this.round.getState().hands[seat], undefined, {
        ...(addedKongTile ? { hiddenTileId: addedKongTile.id } : {}),
        ...(drawn ? { drawnTileId: drawn.id } : {}),
        ...(options?.fadeInDrawnTile ? { fadeInDrawnTile: true } : {}),
      });
    }
  }

  private submitPlayerAction(action: TPlayerAction): void {
    this.traceRound('ui.player-action.submit.before', { action });
    this.playerActionPending = false;
    this.stopTimer();
    this.actionBar.clear();
    try {
      this.round.submitAction(this.playerSeat, action);
      this.traceRound('ui.player-action.submit.after', { action });
    } catch (err) {
      console.error('submitPlayerAction failed', err);
      this.traceRound('ui.player-action.submit.error', {
        action,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  // ----------------------------------------------------------------------
  // 倒數計時 — 統一入口：start 倒數、stop 並清空 timer 文字
  // ----------------------------------------------------------------------

  private startTimer(): void {
    this.turnTimer.start();
  }

  private stopTimer(): void {
    this.turnTimer.stop();
  }
}
